import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import api from '../services/api';
import { useNotification } from '../context/NotificationContext';
import { useUsers } from '../context/UsersContext';
import { usePermissions, PERMISSIONS } from '../hooks/usePermissions';
import { useCheckTypes, useWorkflowStatus, useClarificationTypes } from '../hooks/useMasterData';
import PermissionGuard from '../components/PermissionGuard';
import { formatDateUS, parseDateUS, formatDateTime, formatDateTimeTooltip } from '../utils/dateUtils';
import { MdOutlineHistory } from 'react-icons/md';
import { Info, HelpCircle, Copy } from 'lucide-react';
import ActivityDrawer from '../components/ActivityDrawer';
import SearchableDropdown from '../components/SearchableDropdown';
import USDateInput from '../components/USDateInput';
import UserTimestamp from '../components/UserTimestamp';
import Tooltip from '../components/Tooltip';
import { filterEmojis } from '../utils/emojiFilter';
import './CheckDetails.css';

const CheckDetails = () => {
  const { showError, showSuccess } = useNotification();
  const { id } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { data: checkTypes, loading: checkTypesLoading } = useCheckTypes(true);
  const { data: workflowStatuses, loading: workflowStatusesLoading } = useWorkflowStatus(true);
  const { data: clarificationTypes, loading: clarificationTypesLoading } = useClarificationTypes(true);
  
  // State management
  const [check, setCheck] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('overview');
  const [isEditMode, setIsEditMode] = useState(false);
  const [editingBatchId, setEditingBatchId] = useState(null);
  /** Active locations for the current check's practice only */
  const [transferLocations, setTransferLocations] = useState([]);
  const { users, getUserName, getUserById } = useUsers(); // Get users from context
  const { can } = usePermissions(); // Get permission checker
  const [batchValidationErrors, setBatchValidationErrors] = useState({});
  const [batchSortField, setBatchSortField] = useState('batchDate');
  const [batchSortDirection, setBatchSortDirection] = useState('desc');
  const [showArchiveConfirm, setShowArchiveConfirm] = useState(false);
  const [archiveBatchId, setArchiveBatchId] = useState(null);
  const [archiveBatchIsArchived, setArchiveBatchIsArchived] = useState(false);
  const [showDeleteWarning, setShowDeleteWarning] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleteConfirmBatchId, setDeleteConfirmBatchId] = useState(null);
  const [viewingBatchId, setViewingBatchId] = useState(null);
  const [batchModalMode, setBatchModalMode] = useState('view'); // 'view', 'edit', or 'add'
  const [showDrlAllocationModal, setShowDrlAllocationModal] = useState(false);
  const [selectedBatchForDrlAllocation, setSelectedBatchForDrlAllocation] = useState(null);
  
  // Loading states for prevent multiple submissions
  const [isSaving, setIsSaving] = useState(false);
  const [isCreatingBatch, setIsCreatingBatch] = useState(false);
  const [isUpdatingBatch, setIsUpdatingBatch] = useState(false);
  const [isCreatingClarification, setIsCreatingClarification] = useState(false);
  const [isUpdatingClarification, setIsUpdatingClarification] = useState(false);
  
  // Clarifications state
  const [isAddingClarification, setIsAddingClarification] = useState(false);
  const [editingClarificationId, setEditingClarificationId] = useState(null);
  const [showActivityDrawer, setShowActivityDrawer] = useState(false);
  const [showMoveToUnknownModal, setShowMoveToUnknownModal] = useState(false);
  const [moveToUnknownAccountInput, setMoveToUnknownAccountInput] = useState('');
  const [isMovingToUnknown, setIsMovingToUnknown] = useState(false);
  const hasScrolledToClarification = useRef(false);
  const hasScrolledToBatch = useRef(false);
  const lastClarificationId = useRef(null);
  const lastBatchId = useRef(null);
  
  // Navigation state
  const [checkIds, setCheckIds] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(-1);
  const [navigationLoading, setNavigationLoading] = useState(false);
  
  // Form data
  const [formData, setFormData] = useState({});
  const [batchFormData, setBatchFormData] = useState({
    batchRunNumber: '',
    batchNumber: '',
    batchDate: '',
    batchType: 'MANUAL', // Default to MANUAL
    batchSource: 'Athena',
    batchAmount: '',
    batchNotes: '',
    invoiceNumber: '',
    edmNumber: '',
    correctionBatch: false
  });
  const [clarificationFormData, setClarificationFormData] = useState({
    clarificationType: '',
    details: '',
    assignee: 'ON-SHORE', // Default assignee
    reportee: 'OFF-SHORE', // Default reportee
    assigneeId: '', // Not required for default assignment
    reporterId: '', // Not required for default assignment
    status: 'OPEN',
    amount: '', // Optional amount field
    pageNumber: '',
    patientName: '',
    patientAccountNumber: '',
    insurance: ''
  });
  const [expandedCommentsClarificationId, setExpandedCommentsClarificationId] = useState(null);
  const [commentText, setCommentText] = useState({});
  const [commentAssignee, setCommentAssignee] = useState({});
  const [commentReportee, setCommentReportee] = useState({});
  const [expandedTextareas, setExpandedTextareas] = useState({});
  
  // Store original data for change detection
  const [originalFormData, setOriginalFormData] = useState(null);
  const [originalBatchData, setOriginalBatchData] = useState(null);
  const [originalClarificationData, setOriginalClarificationData] = useState(null);

  // Load check IDs from sessionStorage and find current position
  useEffect(() => {
    const storedCheckIds = sessionStorage.getItem('checksNavigationIds');
    const storedFilters = sessionStorage.getItem('checksNavigationFilters');
    
    if (storedCheckIds) {
      try {
        const ids = JSON.parse(storedCheckIds);
        setCheckIds(ids);
        const index = ids.findIndex(checkId => checkId === id);
        setCurrentIndex(index);
      } catch (err) {
        console.error('Error parsing stored check IDs:', err);
        const errorMessage = err?.message || 'Failed to parse stored check IDs';
        showError(errorMessage);
      }
    } else {
      // If no stored IDs, try to fetch current page
      if (storedFilters) {
        try {
          const filters = JSON.parse(storedFilters);
          const params = {
            ...filters,
            page: filters.page || 0,
            size: 50
          };
          api.getChecksDashboard(params).then(response => {
            const checksList = response.items || [];
            const ids = checksList.map(c => c.checkId);
            setCheckIds(ids);
            const index = ids.findIndex(checkId => checkId === id);
            setCurrentIndex(index);
            // Update sessionStorage
            sessionStorage.setItem('checksNavigationIds', JSON.stringify(ids));
          }).catch(err => {
            console.error('Error fetching checks for navigation:', err);
            const errorMessage = err?.message || err?.data?.message || 'Failed to fetch checks for navigation';
            showError(errorMessage);
          });
        } catch (err) {
          console.error('Error parsing stored filters:', err);
          const errorMessage = err?.message || 'Failed to parse stored filters';
          showError(errorMessage);
        }
      }
    }
  }, [id, showError]);

  // Fetch locations only for the current check's practice
  useEffect(() => {
    if (!check?.practiceCode) {
      setTransferLocations([]);
      return;
    }

    let cancelled = false;

    (async () => {
      try {
        const practicesResponse = await api.getAllPractices();
        const practicesList = Array.isArray(practicesResponse)
          ? practicesResponse
          : (practicesResponse?.items || []);
        const activePractices = practicesList.filter((p) => p.isActive !== false);
        const matchedPractice = activePractices.find((p) => p.code === check.practiceCode);

        if (!matchedPractice) {
          if (!cancelled) setTransferLocations([]);
          return;
        }

        const locationsResponse = await api.getPracticeLocations(matchedPractice.practiceId || matchedPractice.id);
        const locationsList = Array.isArray(locationsResponse) ? locationsResponse : (locationsResponse?.items || []);

        const formattedLocations = locationsList
          .filter((l) => l.isActive !== false)
          .map((l) => ({
            code: l.code,
            name: l.name || l.code,
            practiceCode: matchedPractice.code,
          }))
          .filter((l) => l.code)
          .sort((a, b) =>
            String(a.name || '').localeCompare(String(b.name || ''), undefined, {
              sensitivity: 'base',
            })
          );

        if (!cancelled) setTransferLocations(formattedLocations);
      } catch (error) {
        console.error('Error fetching locations for current practice:', error);
        if (!cancelled) {
          setTransferLocations([]);
          const errorMessage = error?.message || error?.data?.message || 'Failed to fetch practice locations';
          showError(errorMessage);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [check?.practiceCode, showError]);

  // Fetch check details with duplicate call prevention
  const checkDetailsFetchedRef = useRef(false);
  const lastFetchedIdRef = useRef(null);
  useEffect(() => {
    if (id) {
      // Reset the ref if the ID has changed
      if (lastFetchedIdRef.current !== id) {
        checkDetailsFetchedRef.current = false;
        lastFetchedIdRef.current = id;
      }
      
      // Prevent duplicate calls for the same ID
      if (!checkDetailsFetchedRef.current) {
        checkDetailsFetchedRef.current = true;
        fetchCheckDetails();
      }
      // Removed: Users are now loaded from context on login
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  // Check for tab, clarificationId, or batchId in URL and auto-switch tabs
  useEffect(() => {
    const tabParam = searchParams.get('tab');
    const clarificationIdParam = searchParams.get('clarificationId');
    const batchIdParam = searchParams.get('batchId');
    
    if (tabParam && ['overview', 'batches', 'clarifications'].includes(tabParam)) {
      setActiveTab(tabParam);
    } else if (clarificationIdParam && id) {
      setActiveTab('clarifications');
    } else if (batchIdParam && id) {
      setActiveTab('batches');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams, id]);

  // Reset scroll flags when check ID changes
  useEffect(() => {
    hasScrolledToClarification.current = false;
    hasScrolledToBatch.current = false;
    lastClarificationId.current = null;
    lastBatchId.current = null;
  }, [id]);

  // Expand specific clarification after clarifications are loaded
  useEffect(() => {
    const clarificationIdParam = searchParams.get('clarificationId');
    const clarifications = check?.clarifications || [];
    if (clarificationIdParam && clarifications.length > 0 && activeTab === 'clarifications') {
      const clarification = clarifications.find(c => c.clarificationId === clarificationIdParam);
      if (clarification) {
        // Only scroll if this is a new clarification ID or we haven't scrolled yet
        const isNewClarification = lastClarificationId.current !== clarificationIdParam;
        if (isNewClarification || !hasScrolledToClarification.current) {
          lastClarificationId.current = clarificationIdParam;
          hasScrolledToClarification.current = false; // Reset to allow scroll
          
          // Scroll to the clarification card after a short delay to ensure it's rendered
          setTimeout(() => {
            const element = document.querySelector(`[data-clarification-id="${clarificationIdParam}"]`);
            if (element) {
              element.scrollIntoView({ behavior: 'smooth', block: 'center' });
              // Highlight the clarification briefly
              element.style.backgroundColor = '#fef3c7';
              setTimeout(() => {
                element.style.backgroundColor = '';
              }, 2000);
              hasScrolledToClarification.current = true;
            }
          }, 300);
        }
      }
    } else if (!clarificationIdParam) {
      // Reset when clarificationId is removed from URL
      hasScrolledToClarification.current = false;
      lastClarificationId.current = null;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [check?.clarifications, searchParams, activeTab]);

  // Highlight specific batch after batches are loaded
  useEffect(() => {
    const batchIdParam = searchParams.get('batchId');
    if (batchIdParam && check?.batches && check.batches.length > 0 && activeTab === 'batches') {
      const batch = check.batches.find(b => b.batchId === batchIdParam);
      if (batch) {
        // Only scroll if this is a new batch ID or we haven't scrolled yet
        const isNewBatch = lastBatchId.current !== batchIdParam;
        if (isNewBatch || !hasScrolledToBatch.current) {
          lastBatchId.current = batchIdParam;
          hasScrolledToBatch.current = false; // Reset to allow scroll
          
          // Scroll to the batch row after a short delay to ensure it's rendered
          setTimeout(() => {
            const element = document.querySelector(`[data-batch-id="${batchIdParam}"]`);
            if (element) {
              element.scrollIntoView({ behavior: 'smooth', block: 'center' });
              // Highlight the batch row briefly
              element.style.backgroundColor = '#fef3c7';
              setTimeout(() => {
                element.style.backgroundColor = '';
              }, 2000);
              hasScrolledToBatch.current = true;
            }
          }, 300);
        }
      }
    } else if (!batchIdParam) {
      // Reset when batchId is removed from URL
      hasScrolledToBatch.current = false;
      lastBatchId.current = null;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [check?.batches, searchParams, activeTab]);

  const fetchCheckDetails = async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await api.getCheckById(id);
      setCheck(data);
      setFormData({
        altCheckNumber: data.altCheckNumber || '',
        receivedDate: data.receivedDate || '',
        exchange: data.exchange || '',
        batchDescription: data.batchDescription || '',
        payer: data.payer || '',
        bankTrnRef: data.bankTrnRef || '',
        assigneeId: data.assigneeId || '',
        reporterId: data.reporterId || '',
        checkType: data.checkType || '',
        interestAmount: data.interestAmount || 0,
        nonArAmount: data.nonArAmount || 0,
        medicalRecordsFee: data.medicalRecordsFee || 0,
        correctionsAmount: data.correctionsAmount || 0,
        otherAmount: data.otherAmount || 0,
        iodAmount: data.iodAmount || 0,
        miscPaymentAmount: data.miscPaymentAmount || 0,
        pharmacyPaymentAmount: data.pharmacyPaymentAmount || 0,
        legacyNotes: data.legacyNotes || '',
        againstCheckAdditional: data.againstCheckAdditional || '',
        transferInFrom: data.transferInFrom || '',
        transferInAmount: data.transferInAmount || 0,
        transferOutFrom: data.transferOutFrom || '',
        transferOutAmount: data.transferOutAmount || 0
      });
    } catch (err) {
      console.error('Error fetching check details:', err);
      const errorMessage = err?.message || err?.data?.message || 'Failed to load check details. Please try again.';
      setError(errorMessage);
      showError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  // Removed: Users are now loaded from context on login, no need to fetch here
  // Removed: Clarifications are now included in check details, no need for separate API call

  const handleFormChange = (field, value) => {
    // Filter emojis from string inputs
    const filteredValue = typeof value === 'string' ? filterEmojis(value) : value;
    setFormData(prev => ({
      ...prev,
      [field]: filteredValue
    }));
  };

  // Removed unused generateUniqueRandomHex function

  const handleBatchFormChange = (field, value) => {
    // Filter emojis from string inputs
    const filteredValue = typeof value === 'string' ? filterEmojis(value) : value;
    setBatchFormData(prev => {
      const newData = {
        ...prev,
        [field]: filteredValue
      };
      
      // If batchType is changed to MANUAL, clear batchRunNumber
      if (field === 'batchType' && filteredValue === 'MANUAL') {
        newData.batchRunNumber = '';
      }
      
      // If batchType is changed to AUTO, clear EDM number
      if (field === 'batchType' && filteredValue === 'AUTO') {
        newData.edmNumber = '';
      }
      
      // Clear validation errors when field changes
      if (batchValidationErrors[field]) {
        setBatchValidationErrors(prev => {
          const newErrors = { ...prev };
          delete newErrors[field];
          return newErrors;
        });
      }
      
      return newData;
    });
  };
  
  // Validate batch number and run number uniqueness
  const validateBatchUniqueness = () => {
    const errors = {};
    const existingBatches = check?.batches || [];
    const currentBatchId = editingBatchId; // null when adding, batchId when editing
    
    // Check batch number uniqueness
    if (batchFormData.batchNumber && batchFormData.batchNumber.trim()) {
      const duplicateBatch = existingBatches.find(
        b => b.batchNumber && b.batchNumber.trim() === batchFormData.batchNumber.trim() && 
        b.batchId !== currentBatchId
      );
      if (duplicateBatch) {
        errors.batchNumber = 'Batch Number must be unique';
      }
    }
    
    // Check run number uniqueness
    if (batchFormData.batchRunNumber && batchFormData.batchRunNumber.trim()) {
      const duplicateRun = existingBatches.find(
        b => b.batchRunNumber && 
        b.batchRunNumber.trim() === batchFormData.batchRunNumber.trim() && 
        b.batchId !== currentBatchId
      );
      if (duplicateRun) {
        errors.batchRunNumber = 'Run Number must be unique';
      }
    }
    
    setBatchValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };
  
  // Validate on form change
  useEffect(() => {
    if (batchFormData.batchNumber || batchFormData.batchRunNumber) {
      validateBatchUniqueness();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [batchFormData.batchNumber, batchFormData.batchRunNumber, editingBatchId]);

  const handleSave = async () => {
    if (isSaving) return; // Prevent multiple submissions
    try {
      setIsSaving(true);
      // Convert null values to 0 for numeric fields, explicitly include assignee and reporter
      const dataToSave = {
        ...formData,
        assigneeId: formData.assigneeId ?? null,
        reporterId: formData.reporterId ?? null,
        interestAmount: formData.interestAmount === null || formData.interestAmount === undefined ? 0 : formData.interestAmount,
        nonArAmount: formData.nonArAmount === null || formData.nonArAmount === undefined ? 0 : formData.nonArAmount,
        medicalRecordsFee: formData.medicalRecordsFee === null || formData.medicalRecordsFee === undefined ? 0 : formData.medicalRecordsFee,
        correctionsAmount: formData.correctionsAmount === null || formData.correctionsAmount === undefined ? 0 : formData.correctionsAmount,
        otherAmount: formData.otherAmount === null || formData.otherAmount === undefined ? 0 : formData.otherAmount,
        iodAmount: formData.iodAmount === null || formData.iodAmount === undefined ? 0 : formData.iodAmount,
        miscPaymentAmount: formData.miscPaymentAmount === null || formData.miscPaymentAmount === undefined ? 0 : formData.miscPaymentAmount,
        pharmacyPaymentAmount: formData.pharmacyPaymentAmount === null || formData.pharmacyPaymentAmount === undefined ? 0 : formData.pharmacyPaymentAmount,
        transferInAmount: formData.transferInAmount === null || formData.transferInAmount === undefined ? 0 : formData.transferInAmount,
        transferOutAmount: formData.transferOutAmount === null || formData.transferOutAmount === undefined ? 0 : formData.transferOutAmount,
      };
      
      // Check if data has changed
      if (originalFormData) {
        const hasChanges = Object.keys(dataToSave).some(key => {
          const currentValue = dataToSave[key];
          const originalValue = originalFormData[key];
          
          // Handle numeric fields - compare numbers
          if (typeof currentValue === 'number' || typeof originalValue === 'number') {
            return (currentValue || 0) !== (originalValue || 0);
          }
          
          // Handle string fields - compare trimmed strings
          const currentStr = String(currentValue || '').trim();
          const originalStr = String(originalValue || '').trim();
          return currentStr !== originalStr;
        });
        
        if (!hasChanges) {
          // No changes, just exit edit mode without API call
          setIsEditMode(false);
          setOriginalFormData(null);
          return;
        }
      }
      
      await api.updateCheck(id, dataToSave);
      await fetchCheckDetails();
      setIsEditMode(false);
      setOriginalFormData(null);
    } catch (err) {
      console.error('Error updating check:', err);
      const errorMessage = err?.message || err?.data?.message || 'Failed to update check. Please try again.';
      showError(errorMessage);
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancel = () => {
    // Restore original form data without API call
    if (originalFormData) {
      setFormData(JSON.parse(JSON.stringify(originalFormData)));
    }
    setIsEditMode(false);
    setOriginalFormData(null);
  };

  const handleAddBatch = async () => {
    // Prevent multiple submissions
    if (isCreatingBatch) return;
    
    // Validate required fields
    const validationErrors = {};
    const errorMessages = [];

    // Validate batchDate (required)
    if (!batchFormData.batchDate || !batchFormData.batchDate.trim()) {
      validationErrors.batchDate = 'Batch Date is required';
      errorMessages.push('Batch Date is required');
    }

    // Validate batchNumber (required)
    if (!batchFormData.batchNumber || !batchFormData.batchNumber.trim()) {
      validationErrors.batchNumber = 'Batch Number is required';
      errorMessages.push('Batch Number is required');
    }

    // Validate batchSource (required)
    if (!batchFormData.batchSource || !batchFormData.batchSource.trim()) {
      validationErrors.batchSource = 'Source is required';
      errorMessages.push('Source is required');
    }

    // Validate batchAmount (required)
    if (!batchFormData.batchAmount || batchFormData.batchAmount === '') {
      validationErrors.batchAmount = 'Amount is required';
      errorMessages.push('Amount is required');
    } else if (isNaN(parseFloat(batchFormData.batchAmount))) {
      validationErrors.batchAmount = 'Amount must be a valid number';
      errorMessages.push('Amount must be a valid number');
    }

    // Validate invoiceNumber when check is unknown
    const isUnknownCheck = check && check.unknown === true;
    if (isUnknownCheck) {
      if (!batchFormData.invoiceNumber || !batchFormData.invoiceNumber.trim()) {
        validationErrors.invoiceNumber = 'Invoice Number is required for unknown checks';
        errorMessages.push('Invoice Number is required for unknown checks');
      }
    }

    // Validate uniqueness
    if (!validateBatchUniqueness()) {
      if (batchValidationErrors.batchNumber && !validationErrors.batchNumber) {
        validationErrors.batchNumber = batchValidationErrors.batchNumber;
        errorMessages.push(batchValidationErrors.batchNumber);
      }
      if (batchValidationErrors.batchRunNumber) {
        validationErrors.batchRunNumber = batchValidationErrors.batchRunNumber;
        errorMessages.push(batchValidationErrors.batchRunNumber);
      }
    }

    // If there are validation errors, show them and return
    if (Object.keys(validationErrors).length > 0) {
      setBatchValidationErrors(validationErrors);
      showError(errorMessages.join(', '));
      return;
    }
    
    // Double check to prevent race condition
    if (isCreatingBatch) return;
    
    try {
      setIsCreatingBatch(true);
      const batchData = {
        ...batchFormData,
        // USDateInput returns YYYY-MM-DD format, but parseDateUS handles both formats
        batchDate: batchFormData.batchDate ? (parseDateUS(batchFormData.batchDate) || batchFormData.batchDate) : '',
        batchAmount: parseFloat(batchFormData.batchAmount) || 0,
        // batchRunNumber is empty for MANUAL type, otherwise use provided value
        batchRunNumber: batchFormData.batchType === 'MANUAL' ? '' : (batchFormData.batchRunNumber || ''),
        batchSource: batchFormData.batchSource || 'Athena'
      };
      await api.createBatch(id, batchData);
      await fetchCheckDetails();
      setViewingBatchId(null);
      setBatchModalMode('view');
      setBatchValidationErrors({});
      setBatchFormData({
        batchRunNumber: '',
        batchNumber: '',
        batchDate: '',
        batchType: 'MANUAL',
        batchSource: 'Athena',
        batchAmount: '',
        batchNotes: '',
        invoiceNumber: '',
        edmNumber: '',
        correctionBatch: false
      });
    } catch (err) {
      console.error('Error creating batch:', err);
      // Show error message
      const errorMessage = err?.data?.message || err?.message || 'Failed to create batch. Please try again.';
      showError(errorMessage);
    } finally {
      setIsCreatingBatch(false);
    }
  };

  const handleEditBatch = (batch) => {
    setEditingBatchId(batch.batchId);
    const batchType = batch.batchType || 'MANUAL';
    const initialBatchData = {
      // Clear batchRunNumber if batchType is MANUAL
      batchRunNumber: batchType === 'MANUAL' ? '' : (batch.batchRunNumber || ''),
      batchNumber: batch.batchNumber || '',
      // Keep date in YYYY-MM-DD format for USDateInput (it will display as MM/DD/YYYY)
      batchDate: batch.batchDate || '',
      batchType: batchType,
      batchSource: batch.batchSource || 'Athena',
      batchAmount: batch.batchAmount || '',
      batchNotes: batch.batchNotes || '',
      invoiceNumber: batch.invoiceNumber || '',
      edmNumber: batch.edmNumber || '',
      correctionBatch: batch.correctionBatch || batch.isCorrectionBatch || false
    };
    setBatchFormData(initialBatchData);
    // Store original batch data for change detection
    setOriginalBatchData(JSON.parse(JSON.stringify(initialBatchData)));
    setBatchValidationErrors({});
  };

  const handleUpdateBatch = async () => {
    // Validate required fields
    const validationErrors = {};
    const errorMessages = [];

    // Validate batchDate (required)
    if (!batchFormData.batchDate || !batchFormData.batchDate.trim()) {
      validationErrors.batchDate = 'Batch Date is required';
      errorMessages.push('Batch Date is required');
    }

    // Validate batchNumber (required)
    if (!batchFormData.batchNumber || !batchFormData.batchNumber.trim()) {
      validationErrors.batchNumber = 'Batch Number is required';
      errorMessages.push('Batch Number is required');
    }

    // Validate batchSource (required)
    if (!batchFormData.batchSource || !batchFormData.batchSource.trim()) {
      validationErrors.batchSource = 'Source is required';
      errorMessages.push('Source is required');
    }

    // Validate batchAmount (required)
    if (!batchFormData.batchAmount || batchFormData.batchAmount === '') {
      validationErrors.batchAmount = 'Amount is required';
      errorMessages.push('Amount is required');
    } else if (isNaN(parseFloat(batchFormData.batchAmount))) {
      validationErrors.batchAmount = 'Amount must be a valid number';
      errorMessages.push('Amount must be a valid number');
    }

    // Validate invoiceNumber when check is unknown
    const isUnknownCheck = check && check.unknown === true;
    if (isUnknownCheck) {
      if (!batchFormData.invoiceNumber || !batchFormData.invoiceNumber.trim()) {
        validationErrors.invoiceNumber = 'Invoice Number is required for unknown checks';
        errorMessages.push('Invoice Number is required for unknown checks');
      }
    }

    // Validate uniqueness
    if (!validateBatchUniqueness()) {
      if (batchValidationErrors.batchNumber && !validationErrors.batchNumber) {
        validationErrors.batchNumber = batchValidationErrors.batchNumber;
        errorMessages.push(batchValidationErrors.batchNumber);
      }
      if (batchValidationErrors.batchRunNumber) {
        validationErrors.batchRunNumber = batchValidationErrors.batchRunNumber;
        errorMessages.push(batchValidationErrors.batchRunNumber);
      }
    }

    // If there are validation errors, show them and return
    if (Object.keys(validationErrors).length > 0) {
      setBatchValidationErrors(validationErrors);
      showError(errorMessages.join(', '));
      return;
    }
    
    if (isUpdatingBatch) return; // Prevent multiple submissions
    
    try {
      setIsUpdatingBatch(true);
      const batchData = {
        ...batchFormData,
        // USDateInput returns YYYY-MM-DD format, but parseDateUS handles both formats
        batchDate: batchFormData.batchDate ? (parseDateUS(batchFormData.batchDate) || batchFormData.batchDate) : '',
        batchAmount: parseFloat(batchFormData.batchAmount) || 0,
        // batchRunNumber is empty for MANUAL type, otherwise use provided value
        batchRunNumber: batchFormData.batchType === 'MANUAL' ? '' : (batchFormData.batchRunNumber || ''),
        batchSource: batchFormData.batchSource || 'Athena',
        // EDM number is only for MANUAL type, otherwise empty
        edmNumber: batchFormData.batchType === 'MANUAL' ? (batchFormData.edmNumber || '') : '',
        correctionBatch: batchFormData.correctionBatch || false
      };
      
      // Check if data has changed
      if (originalBatchData) {
        const normalizedOriginal = {
          ...originalBatchData,
          batchDate: originalBatchData.batchDate ? (parseDateUS(originalBatchData.batchDate) || originalBatchData.batchDate) : '',
          batchAmount: parseFloat(originalBatchData.batchAmount) || 0,
          batchRunNumber: originalBatchData.batchType === 'MANUAL' ? '' : (originalBatchData.batchRunNumber || ''),
          batchSource: originalBatchData.batchSource || 'Athena'
        };
        
        const hasChanges = Object.keys(batchData).some(key => {
          const currentValue = batchData[key];
          const originalValue = normalizedOriginal[key];
          
          // Handle numeric fields - compare numbers
          if (typeof currentValue === 'number' || typeof originalValue === 'number') {
            return (currentValue || 0) !== (originalValue || 0);
          }
          
          // Handle string fields - compare trimmed strings
          const currentStr = String(currentValue || '').trim();
          const originalStr = String(originalValue || '').trim();
          return currentStr !== originalStr;
        });
        
        if (!hasChanges) {
          // No changes, just exit edit mode without API call
          setEditingBatchId(null);
          setOriginalBatchData(null);
          setBatchValidationErrors({});
          setBatchFormData({
            batchRunNumber: '',
            batchNumber: '',
            batchDate: '',
            batchType: 'MANUAL',
            batchSource: 'Athena',
            batchAmount: '',
            batchNotes: '',
            invoiceNumber: '',
            edmNumber: '',
            correctionBatch: false
          });
          return;
        }
      }
      
      await api.updateBatch(id, editingBatchId, batchData);
      await fetchCheckDetails();
      setEditingBatchId(null);
      setOriginalBatchData(null);
      setBatchValidationErrors({});
      setBatchFormData({
        batchRunNumber: '',
        batchNumber: '',
        batchDate: '',
        batchType: 'MANUAL',
        batchSource: 'Athena',
        batchAmount: '',
        batchNotes: '',
        invoiceNumber: '',
        edmNumber: '',
        correctionBatch: false
      });
    } catch (err) {
      console.error('Error updating batch:', err);
      // Show error message
      const errorMessage = err?.data?.message || err?.message || 'Failed to update batch. Please try again.';
      showError(errorMessage);
    } finally {
      setIsUpdatingBatch(false);
    }
  };
  
  const handleArchiveBatchClick = (batchId, isArchived) => {
    setArchiveBatchId(batchId);
    setArchiveBatchIsArchived(isArchived);
    setShowArchiveConfirm(true);
  };
  
  const handleArchiveBatchConfirm = async () => {
    if (!archiveBatchId) return;
    
    // Check permission
    if (!can(PERMISSIONS.CHECK_BATCH_UPDATE)) {
      alert('You do not have permission to archive/unarchive batches.');
      setShowArchiveConfirm(false);
      setArchiveBatchId(null);
      setArchiveBatchIsArchived(false);
      return;
    }
    
    try {
      await api.archiveBatch(id, archiveBatchId, !archiveBatchIsArchived);
      await fetchCheckDetails();
      setShowArchiveConfirm(false);
      setArchiveBatchId(null);
      setArchiveBatchIsArchived(false);
    } catch (err) {
      console.error('Error archiving batch:', err);
      const errorMessage = err?.data?.message || err?.message || `Failed to ${archiveBatchIsArchived ? 'unarchive' : 'archive'} batch. Please try again.`;
      showError(errorMessage);
    }
  };
  
  const handleArchiveBatchCancel = () => {
    setShowArchiveConfirm(false);
    setArchiveBatchId(null);
    setArchiveBatchIsArchived(false);
  };
  
  const handleDeleteBatch = async (batchId, isArchived) => {
    if (!can(PERMISSIONS.CHECK_BATCH_DELETE)) {
      alert('You do not have permission to delete batches.');
      return;
    }
    
    // Check if batch is archived
    if (!isArchived) {
      setShowDeleteWarning(true);
      return;
    }
    
    // Show delete confirmation modal
    setDeleteConfirmBatchId(batchId);
    setShowDeleteConfirm(true);
  };

  const handleDeleteConfirm = async () => {
    if (!deleteConfirmBatchId) return;
    
    try {
      await api.deleteBatch(id, deleteConfirmBatchId);
      await fetchCheckDetails();
      setShowDeleteConfirm(false);
      setDeleteConfirmBatchId(null);
    } catch (err) {
      console.error('Error deleting batch:', err);
      const errorMessage = err?.data?.message || err?.message || 'Failed to delete batch. Please try again.';
      showError(errorMessage);
      setShowDeleteConfirm(false);
      setDeleteConfirmBatchId(null);
    }
  };

  const handleDeleteConfirmCancel = () => {
    setShowDeleteConfirm(false);
    setDeleteConfirmBatchId(null);
  };

  const handleDeleteWarningClose = () => {
    setShowDeleteWarning(false);
  };
  
  const handleSortBatches = (field) => {
    if (batchSortField === field) {
      setBatchSortDirection(prev => prev === 'asc' ? 'desc' : 'asc');
    } else {
      setBatchSortField(field);
      setBatchSortDirection('asc');
    }
  };
  
  const getSortArrow = (field) => {
    if (batchSortField !== field) return null;
    return <span className="sort-arrow">{batchSortDirection === 'asc' ? '↑' : '↓'}</span>;
  };
  
  // Get sorted batches
  const getSortedBatches = () => {
    if (!check?.batches || check.batches.length === 0) return [];
    
    const batches = [...check.batches];
    return batches.sort((a, b) => {
      let aVal = a[batchSortField];
      let bVal = b[batchSortField];
      
      if (batchSortField === 'batchDate' || batchSortField === 'createdAt' || batchSortField === 'updatedAt') {
        aVal = aVal ? new Date(aVal) : new Date(0);
        bVal = bVal ? new Date(bVal) : new Date(0);
      } else if (batchSortField === 'batchAmount') {
        aVal = aVal || 0;
        bVal = bVal || 0;
      } else {
        aVal = aVal || '';
        bVal = bVal || '';
      }
      
      if (batchSortDirection === 'asc') {
        return aVal > bVal ? 1 : aVal < bVal ? -1 : 0;
      } else {
        return aVal < bVal ? 1 : aVal > bVal ? -1 : 0;
      }
    });
  };

  const handleCancelBatch = () => {
    // Restore original batch data if editing (not adding)
    if (editingBatchId && originalBatchData) {
      setBatchFormData(JSON.parse(JSON.stringify(originalBatchData)));
    } else {
      // Reset to default if adding new batch
      setBatchFormData({
        batchRunNumber: '',
        batchNumber: '',
        batchDate: '',
        batchType: 'MANUAL',
        batchSource: 'Athena',
        batchAmount: '',
        batchNotes: '',
        invoiceNumber: '',
        edmNumber: '',
        correctionBatch: false
      });
    }
    setEditingBatchId(null);
    setOriginalBatchData(null);
    setBatchValidationErrors({});
    // Close the modal by resetting viewing state
    setViewingBatchId(null);
    setBatchModalMode('view');
  };

  // Clarification handlers
  const handleClarificationFormChange = (field, value) => {
    // Filter emojis from string inputs
    const filteredValue = typeof value === 'string' ? filterEmojis(value) : value;
    
    // Auto-select OFF-SHORE in reporter when ON-SHORE is selected for assignee
    if (field === 'assignee' && value) {
      setClarificationFormData(prev => ({
        ...prev,
        [field]: filteredValue,
        reportee: filteredValue === 'ON-SHORE' ? 'OFF-SHORE' : prev.reportee // Auto-select OFF-SHORE if ON-SHORE selected
      }));
    } else {
      setClarificationFormData(prev => ({
        ...prev,
        [field]: filteredValue
      }));
    }
  };

  const toggleCommentsExpanded = (clarificationId) => {
    setExpandedCommentsClarificationId(prev => 
      prev === clarificationId ? null : clarificationId
    );
  };

  const handleAddClarification = async () => {
    // Prevent multiple submissions
    if (isCreatingClarification) return;
    
    // Validate required fields - clarification type is required
    if (!clarificationFormData.clarificationType || !clarificationFormData.clarificationType.trim()) {
      showError('Clarification Type is required');
      return;
    }
    
    try {
      setIsCreatingClarification(true);
      // Convert amount to number (0 if empty)
      const formDataToSend = {
        ...clarificationFormData,
        amount: clarificationFormData.amount && clarificationFormData.amount !== '' 
          ? parseFloat(clarificationFormData.amount) || 0 
          : 0
      };
      await api.createClarification(id, formDataToSend);
      await fetchCheckDetails(); // Refresh check data to get updated clarifications
      setIsAddingClarification(false);
      setClarificationFormData({
        clarificationType: '',
        details: '',
        assignee: 'ON-SHORE', // Default assignee
        reportee: 'OFF-SHORE', // Default reportee
        assigneeId: '', // Not required for default assignment
        reporterId: '', // Not required for default assignment
        status: 'OPEN',
        amount: '', // Optional amount field
        pageNumber: '',
        patientName: '',
        patientAccountNumber: '',
        insurance: ''
      });
    } catch (err) {
      console.error('Error creating clarification:', err);
      const errorMessage = err?.message || err?.data?.message || 'Failed to create clarification. Please try again.';
      showError(errorMessage);
    } finally {
      setIsCreatingClarification(false);
    }
  };

  const handleEditClarification = (clarification) => {
    setEditingClarificationId(clarification.clarificationId);
    // Automatically expand comments when editing
    setExpandedCommentsClarificationId(clarification.clarificationId);
    const initialClarificationData = {
      clarificationType: clarification.clarificationType || '',
      details: clarification.details || '',
      assignee: clarification.assignee || 'ON-SHORE', // Default to ON-SHORE if not set
      reportee: clarification.reportee || 'OFF-SHORE', // Default to OFF-SHORE if not set
      assigneeId: clarification.assigneeId || '',
      reporterId: clarification.reporterId || '',
      status: clarification.status || 'OPEN',
      amount: clarification.amount !== undefined && clarification.amount !== null ? clarification.amount : '',
      pageNumber: clarification.pageNumber || '',
      patientName: clarification.patientName || '',
      patientAccountNumber: clarification.patientAccountNumber || '',
      insurance: clarification.insurance || ''
    };
    setClarificationFormData(initialClarificationData);
    // Store original clarification data for change detection
    setOriginalClarificationData(JSON.parse(JSON.stringify(initialClarificationData)));
  };

  const handleUpdateClarification = async () => {
    // Prevent multiple submissions
    if (isUpdatingClarification) return;
    
    try {
      // Check if data has changed
      if (originalClarificationData) {
        const hasChanges = Object.keys(clarificationFormData).some(key => {
          const currentValue = clarificationFormData[key];
          const originalValue = originalClarificationData[key];
          
          // Handle string fields - compare trimmed strings
          const currentStr = String(currentValue || '').trim();
          const originalStr = String(originalValue || '').trim();
          return currentStr !== originalStr;
        });
        
        if (!hasChanges) {
          // No changes, just exit edit mode without API call
          const previousClarificationId = editingClarificationId;
          setEditingClarificationId(null);
          setOriginalClarificationData(null);
          // Keep comments expanded after update
          setExpandedCommentsClarificationId(previousClarificationId);
          setClarificationFormData({
            clarificationType: '',
            details: '',
            assignee: 'ON-SHORE', // Default assignee
            reportee: 'EBOTICS', // Default reportee
            assigneeId: '', // Not required for default assignment
            reporterId: '', // Not required for default assignment
            status: 'OPEN',
            amount: '', // Optional amount field
            pageNumber: '',
            patientName: '',
            patientAccountNumber: '',
            insurance: ''
          });
          return;
        }
      }
      
      // Validate required fields - clarification type is required
      if (!clarificationFormData.clarificationType || !clarificationFormData.clarificationType.trim()) {
        showError('Clarification Type is required');
        return;
      }
      
      setIsUpdatingClarification(true);
      // Convert amount to number (0 if empty)
      const formDataToSend = {
        ...clarificationFormData,
        amount: clarificationFormData.amount && clarificationFormData.amount !== '' 
          ? parseFloat(clarificationFormData.amount) || 0 
          : 0
      };
      await api.updateClarification(id, editingClarificationId, formDataToSend);
      await fetchCheckDetails(); // Refresh check data to get updated clarifications
      const previousClarificationId = editingClarificationId;
      setEditingClarificationId(null);
      setOriginalClarificationData(null);
      // Keep comments expanded after update
      setExpandedCommentsClarificationId(previousClarificationId);
      setClarificationFormData({
        clarificationType: '',
        details: '',
        assignee: 'ON-SHORE', // Default assignee
        reportee: 'OFF-SHORE', // Default reportee
        assigneeId: '', // Not required for default assignment
        reporterId: '', // Not required for default assignment
        status: 'OPEN',
        amount: '', // Optional amount field
        pageNumber: '',
        patientName: '',
        patientAccountNumber: '',
        insurance: ''
      });
    } catch (err) {
      console.error('Error updating clarification:', err);
      const errorMessage = err?.message || err?.data?.message || 'Failed to update clarification. Please try again.';
      showError(errorMessage);
    } finally {
      setIsUpdatingClarification(false);
    }
  };

  const handleCancelClarification = () => {
    setIsAddingClarification(false);
    // Restore original clarification data if editing (not adding)
    if (editingClarificationId && originalClarificationData) {
      setClarificationFormData(JSON.parse(JSON.stringify(originalClarificationData)));
      // Keep comments expanded when canceling edit
    } else {
      // Reset to default if adding new clarification
      // Collapse comments when canceling add
      setExpandedCommentsClarificationId(null);
      setClarificationFormData({
        clarificationType: '',
        details: '',
        assignee: 'ON-SHORE', // Default assignee
        reportee: 'OFF-SHORE', // Default reportee
        assigneeId: '', // Not required for default assignment
        reporterId: '', // Not required for default assignment
        status: 'OPEN',
        amount: '', // Optional amount field
        pageNumber: '',
        patientName: '',
        patientAccountNumber: '',
        insurance: ''
      });
    }
    setEditingClarificationId(null);
    setOriginalClarificationData(null);
  };

  const handleCopyClarificationId = async (clarificationId) => {
    if (!clarificationId) return;
    try {
      await navigator.clipboard.writeText(clarificationId);
      showSuccess('Clarification ID copied');
    } catch (err) {
      console.error('Copy failed:', err);
      showError('Could not copy clarification ID');
    }
  };

  const handleAddComment = async (clarificationId) => {
    const comment = commentText[clarificationId];
    const assignee = commentAssignee[clarificationId];
    let reportee = commentReportee[clarificationId];
    
    // Auto-set reporter based on assignee if assignee is selected
    if (assignee && !reportee) {
      reportee = assignee === 'ON-SHORE' ? 'OFF-SHORE' : 'ON-SHORE';
      // Update state to reflect the auto-set reporter
      setCommentReportee(prev => ({
        ...prev,
        [clarificationId]: reportee
      }));
    }
    
    // At least comment or assignee must be provided
    if ((!comment || !comment.trim()) && !assignee) {
      showError('Please enter a comment or select an assignee');
      return;
    }

    try {
      const commentData = {};
      if (comment && comment.trim()) {
        commentData.newComment = comment.trim();
      }
      if (assignee) {
        commentData.assignee = assignee;
        // Always set reporter when assignee is set
        if (reportee) {
          commentData.reportee = reportee;
        }
      }
      
      await api.addClarificationComment(id, clarificationId, commentData);
      await fetchCheckDetails(); // Refresh check data to get updated clarifications
      
      // Clear comment form
      setCommentText(prev => ({
        ...prev,
        [clarificationId]: ''
      }));
      setCommentAssignee(prev => {
        const updated = { ...prev };
        delete updated[clarificationId];
        return updated;
      });
      setCommentReportee(prev => {
        const updated = { ...prev };
        delete updated[clarificationId];
        return updated;
      });
    } catch (err) {
      console.error('Error adding comment:', err);
      const errorMessage = err?.message || err?.data?.message || 'Failed to add comment. Please try again.';
      showError(errorMessage);
    }
  };


  // Navigation functions
  const handlePreviousCheck = async () => {
    if (currentIndex > 0 && checkIds.length > 0) {
      const previousId = checkIds[currentIndex - 1];
      setNavigationLoading(true);
      navigate(`/checks/${previousId}`);
    } else if (currentIndex === 0 && checkIds.length > 0) {
      // Try to fetch previous page
      const storedFilters = sessionStorage.getItem('checksNavigationFilters');
      if (storedFilters) {
        try {
          const filters = JSON.parse(storedFilters);
          const currentPage = filters.page || 0;
          if (currentPage > 0) {
            // Fetch previous page
            const params = {
              ...filters,
              page: currentPage - 1,
              size: 50
            };
            setNavigationLoading(true);
            const response = await api.getChecksDashboard(params);
            const checksList = response.items || [];
            if (checksList.length > 0) {
              const lastCheckId = checksList[checksList.length - 1].checkId;
              // Update sessionStorage - prepend previous page checks
              const allIds = [...checksList.map(c => c.checkId), ...checkIds];
              sessionStorage.setItem('checksNavigationIds', JSON.stringify(allIds));
              sessionStorage.setItem('checksNavigationFilters', JSON.stringify({ ...filters, page: currentPage - 1 }));
              navigate(`/checks/${lastCheckId}`);
            } else {
              setNavigationLoading(false);
            }
          } else {
            setNavigationLoading(false);
          }
        } catch (err) {
          console.error('Error fetching previous page:', err);
          const errorMessage = err?.message || err?.data?.message || 'Failed to fetch previous page';
          showError(errorMessage);
          setNavigationLoading(false);
        }
      } else {
        setNavigationLoading(false);
      }
    } else {
      setNavigationLoading(false);
    }
  };

  const handleNextCheck = async () => {
    if (currentIndex >= 0 && currentIndex < checkIds.length - 1) {
      const nextId = checkIds[currentIndex + 1];
      setNavigationLoading(true);
      navigate(`/checks/${nextId}`);
    } else if (currentIndex === checkIds.length - 1 && checkIds.length > 0) {
      // Try to fetch next page
      const storedFilters = sessionStorage.getItem('checksNavigationFilters');
      if (storedFilters) {
        try {
          const filters = JSON.parse(storedFilters);
          const currentPage = filters.page || 0;
          const params = {
            ...filters,
            page: currentPage + 1,
            size: 50
          };
          setNavigationLoading(true);
          const response = await api.getChecksDashboard(params);
          const checksList = response.items || [];
          if (checksList.length > 0) {
            const firstCheckId = checksList[0].checkId;
            // Update sessionStorage - append next page checks
            const allIds = [...checkIds, ...checksList.map(c => c.checkId)];
            sessionStorage.setItem('checksNavigationIds', JSON.stringify(allIds));
            sessionStorage.setItem('checksNavigationFilters', JSON.stringify({ ...filters, page: currentPage + 1 }));
            navigate(`/checks/${firstCheckId}`);
          } else {
            setNavigationLoading(false);
          }
        } catch (err) {
          console.error('Error fetching next page:', err);
          const errorMessage = err?.message || err?.data?.message || 'Failed to fetch next page';
          showError(errorMessage);
          setNavigationLoading(false);
        }
      } else {
        setNavigationLoading(false);
      }
    } else {
      setNavigationLoading(false);
    }
  };

  const openMoveToUnknownModal = () => {
    setMoveToUnknownAccountInput(
      check?.unknownAccountNumber != null && check.unknownAccountNumber !== ''
        ? String(check.unknownAccountNumber)
        : ''
    );
    setShowMoveToUnknownModal(true);
  };

  const closeMoveToUnknownModal = () => {
    if (isMovingToUnknown) return;
    setShowMoveToUnknownModal(false);
    setMoveToUnknownAccountInput('');
  };

  const confirmMoveToUnknown = async () => {
    const trimmed = moveToUnknownAccountInput.trim();
    try {
      setIsMovingToUnknown(true);
      const result = await api.moveCheckToUnknown(id, trimmed === '' ? undefined : trimmed);
      const successMessage =
        result?.message ||
        (check?.checkNumber
          ? `Check "${check.checkNumber}" was moved to unknown.`
          : 'Check was moved to unknown successfully.');
      showSuccess(successMessage);
      setShowMoveToUnknownModal(false);
      setMoveToUnknownAccountInput('');
      navigate('/checks');
    } catch (err) {
      console.error('Error moving check to unknown:', err);
      const errorMessage =
        err?.message || err?.data?.message || 'Failed to move check to unknown. Please try again.';
      showError(errorMessage);
    } finally {
      setIsMovingToUnknown(false);
    }
  };

  // Reset navigation loading when check changes
  useEffect(() => {
    setNavigationLoading(false);
  }, [id]);

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2
    }).format(amount || 0);
  };

  const getStatusClass = (status) => {
    const statusMap = {
      'COMPLETED': 'status-complete',
      'UNDER_CLARIFICATIONS': 'status-clarification',
      'IN_PROGRESS': 'status-progress',
      'NOT_STARTED': 'status-not-started',
      'OVER_POSTED': 'status-over-posted'
    };
    return statusMap[status] || 'status-not-started';
  };

  const formatStatus = (status) => {
    const statusMap = {
      'COMPLETED': 'Completed',
      'UNDER_CLARIFICATIONS': 'Under Clarifications',
      'IN_PROGRESS': 'In Progress',
      'NOT_STARTED': 'Not Started',
      'OVER_POSTED': 'Over Posted'
    };
    return statusMap[status] || status;
  };

  // Helper function to truncate text and show tooltip
  const TruncatedText = ({ text, maxLength = 15 }) => {
    if (!text || text === 'N/A') return <span>{text || 'N/A'}</span>;
    
    const textStr = String(text);
    const shouldTruncate = textStr.length > maxLength;
    const displayText = shouldTruncate ? textStr.substring(0, maxLength) + '...' : textStr;
    
    if (shouldTruncate) {
      return (
        <Tooltip text={textStr} position="top">
          <span className="batch-truncated-text">{displayText}</span>
        </Tooltip>
      );
    }
    
    return <span>{displayText}</span>;
  };

  // DRL Allocation helper functions
  const hasDrlAllocations = (batch) => {
    return batch.drlAllocations && batch.drlAllocations.length > 0;
  };

  const getDrlAllocationCount = (batch) => {
    return batch.drlAllocations ? batch.drlAllocations.length : 0;
  };

  /** Transfer In/Out From store location code; fall back to practice name if legacy data used practice code */
  const formatTransferFromDisplay = (code) => {
    if (!code) return 'N/A';
    const loc = transferLocations.find((l) => l.code === code);
    if (loc) return loc.name;
    return code;
  };

  // Navigation loading overlay
  if (navigationLoading) {
    return (
      <div className={`check-details-page ${showActivityDrawer ? 'activity-sidebar-open' : ''}`}>
        <div className="navigation-loading-overlay">
          <div className="navigation-loading-content">
            <div className="navigation-loading-spinner"></div>
            <p className="navigation-loading-text">Loading check details...</p>
          </div>
        </div>
      </div>
    );
  }

  // Skeleton loader
  if (loading) {
    return (
      <div className={`check-details-page ${showActivityDrawer ? 'activity-sidebar-open' : ''}`}>
        <div className="skeleton-loader">
          <div className="skeleton-header"></div>
          <div className="skeleton-card">
            <div className="skeleton-line"></div>
            <div className="skeleton-line"></div>
            <div className="skeleton-line"></div>
          </div>
        </div>
      </div>
    );
  }

  if (error || !check) {
    return (
      <div className={`check-details-page ${showActivityDrawer ? 'activity-sidebar-open' : ''}`}>
        <div className="error-state">
          <p>{error || 'Check not found'}</p>
          <button 
            className="btn-primary" 
            onClick={() => {
              const source = searchParams.get('source');
              if (source === 'clarifications') {
                navigate('/clarifications');
              } else if (source === 'unknown') {
                navigate('/unknown');
              } else {
                navigate('/checks');
              }
            }}
          >
            Back to {searchParams.get('source') === 'clarifications' ? 'Clarifications' : searchParams.get('source') === 'unknown' ? 'Unknown' : 'Checks'}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className={`check-details-page ${showActivityDrawer ? 'activity-sidebar-open' : ''}`}>
      {/* Header */}
      <div className="page-header">
        <div className="header-left">
          <div className="header-top-row">
            <button 
              className="back-btn" 
              onClick={() => {
                const source = searchParams.get('source');
                if (source === 'clarifications') {
                  navigate('/clarifications');
                } else if (source === 'unknown') {
                  navigate('/unknown');
                } else {
                  navigate('/checks');
                }
              }}
            >
              <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                <path d="M12.5 15L7.5 10L12.5 5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
              Back
            </button>
            <div className="title-row">
              <h1 className="page-title">
                {check.checkNumber || 'Check Details'}
              </h1>
              <span className={`status-badge-large ${getStatusClass(check.status)}`}>
                {formatStatus(check.status)}
              </span>
              {check.unknown === true && (
                <Tooltip text="This check has an unknown status">
                  <div style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', padding: '3px 10px', borderRadius: '12px', background: '#fed7aa', border: '1px solid #fb923c', color: '#c2410c', fontSize: '11px', fontWeight: 600, cursor: 'default' }}>
                    <HelpCircle size={14} style={{ flexShrink: 0 }} />
                    <span>Unknown Check</span>
                  </div>
                </Tooltip>
              )}
            </div>
          </div>
          <div className="metadata">
            <span>Payer: {check.payer || 'N/A'}</span>
            <span>Practice: {check.practiceCode || 'N/A'}</span>
            <span>Location: {check.locationCode || 'N/A'}</span>
            <span>Bank TRN Ref: {check.bankTrnRef || 'N/A'}</span>
          </div>
        </div>
        <div className="header-right">
          <div className="header-toolbar">
            {searchParams.get('source') !== 'unknown' && check?.unknown !== true && (
              <button
                type="button"
                className="header-toolbar-btn-teal"
                onClick={openMoveToUnknownModal}
                title="Move this check to unknown"
              >
                <span>Move To Unknown</span>
              </button>
            )}
            {searchParams.get('source') === 'unknown' && check?.batches && check.batches.some(batch => batch.invoiceNumber) && (
              <button 
                type="button"
                className="header-toolbar-btn-teal"
                onClick={async () => {
                  try {
                    await api.updateCheck(id, { unknown: false });
                    await fetchCheckDetails();
                    navigate('/unknown');
                  } catch (err) {
                    console.error('Error marking check as resolved:', err);
                    const errorMessage = err?.message || err?.data?.message || 'Failed to mark check as resolved. Please try again.';
                    showError(errorMessage);
                  }
                }}
              >
                <span>Mark As Resolved</span>
              </button>
            )}
            <div className="navigation-arrows">
              <button 
                className="nav-arrow-btn"
                onClick={handlePreviousCheck}
                disabled={navigationLoading || (currentIndex <= 0 && checkIds.length > 0)}
                title="Previous check"
              >
                <svg width="16" height="16" viewBox="0 0 20 20" fill="none">
                  <path d="M12.5 15L7.5 10L12.5 5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
                <span>Previous</span>
              </button>
              <button 
                className="nav-arrow-btn"
                onClick={handleNextCheck}
                disabled={navigationLoading || (currentIndex >= checkIds.length - 1 && checkIds.length > 0)}
                title="Next check"
              >
                <span>Next</span>
                <svg width="16" height="16" viewBox="0 0 20 20" fill="none">
                  <path d="M7.5 5L12.5 10L7.5 15" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </button>
            </div>
            <button 
              type="button"
              className="activity-btn" 
              onClick={() => setShowActivityDrawer(true)}
              title="View Activity History"
            >
              <MdOutlineHistory size={16} />
              <span>Activity</span>
            </button>
          </div>
        </div>
      </div>

      {/* Check Summary Card */}
      <div className="financial-summary-card merged">
        <div className="financial-sections">
          <div className="financial-section check-summary-section" style={{ width: '100%' }}>
            <h3 className="section-title">Check Summary</h3>
            <div className="financial-grid check-summary-grid-two-rows">
              {/* Row 1: Deposit date, Received date, Complete date, Total amount, Posted amount */}
              <div className="financial-item">
                <span className="financial-label">Deposit date</span>
                <span className="financial-value">
                  {check.depositDate ? formatDateUS(check.depositDate) : 'N/A'}
                </span>
              </div>
              <div className="financial-item">
                <span className="financial-label">Received date</span>
                <span className="financial-value">
                  {check.receivedDate ? formatDateUS(check.receivedDate) : 'N/A'}
                </span>
              </div>
              <div className="financial-item">
                <span className="financial-label">Complete date</span>
                <span className="financial-value">
                  {check.completedDate ? formatDateUS(check.completedDate) : 'N/A'}
                </span>
              </div>
              <div className="financial-item">
                <span className="financial-label">Total amount</span>
                <span className={`financial-value ${(check?.totalAmount || 0) < 0 ? 'negative' : 'primary'}`}>
                  {formatCurrency(check?.totalAmount || 0)}
                </span>
              </div>
              <div className="financial-item">
                <span className="financial-label">Posted amount</span>
                <span className={`financial-value ${(check?.postedAmount || 0) < 0 ? 'negative' : 'success'}`}>
                  {formatCurrency(check?.postedAmount || 0)}
                </span>
              </div>
              {/* Row 2: Updated date, TAT by received date, TAT by deposit date, Other Amount, Remaining amount */}
              <div className="financial-item">
                <span className="financial-label">Updated date</span>
                <span className="financial-value" style={{ display: 'inline-flex', alignItems: 'center', gap: '6px' }}>
                  {check.updatedAt ? (
                    <>
                      {formatDateUS(check.updatedAt.split('T')[0])}
                      <Tooltip
                        text={check.updatedBy ? `${getUserName(check.updatedBy)} - ${formatDateTimeTooltip(check.updatedAt)}` : formatDateTimeTooltip(check.updatedAt)}
                        position="top"
                      >
                        <Info
                          size={16}
                          style={{
                            color: '#374151',
                            cursor: 'pointer',
                            transition: 'color 0.2s',
                            flexShrink: 0
                          }}
                          onMouseEnter={(e) => e.target.style.color = '#0d9488'}
                          onMouseLeave={(e) => e.target.style.color = '#374151'}
                        />
                      </Tooltip>
                    </>
                  ) : 'N/A'}
                </span>
              </div>
              <div className="financial-item">
                <span className="financial-label">TAT by received date</span>
                <span className="financial-value">
                  {typeof check.turnaroundByReceivedDays === 'number'
                    ? `${check.turnaroundByReceivedDays} day${check.turnaroundByReceivedDays === 1 ? '' : 's'}`
                    : 'N/A'}
                </span>
              </div>
              <div className="financial-item">
                <span className="financial-label">TAT by deposit date</span>
                <span className="financial-value">
                  {typeof check.turnaroundByDepositDays === 'number'
                    ? `${check.turnaroundByDepositDays} day${check.turnaroundByDepositDays === 1 ? '' : 's'}`
                    : 'N/A'}
                </span>
              </div>
              <div className="financial-item">
                <span className="financial-label">Other Amount</span>
                <span className={`financial-value ${(check?.otherAmount || 0) < 0 ? 'negative' : ''}`}>
                  {formatCurrency(check?.otherAmount || 0)}
                </span>
              </div>
              <div className="financial-item">
                <span className="financial-label">Remaining amount</span>
                <span className={`financial-value ${(check?.remainingAmount || 0) < 0 ? 'negative' : ''}`}>
                  {formatCurrency(check?.remainingAmount || 0)}
                </span>
              </div>
            </div>
          </div>
          
        </div>
      </div>

      {/* Tabs */}
      <div className="tabs-container">
        <div className="tabs">
          <button 
            className={`tab ${activeTab === 'overview' ? 'active' : ''}`}
            onClick={() => setActiveTab('overview')}
          >
            Overview
          </button>
          <button 
            className={`tab ${activeTab === 'batches' ? 'active' : ''}`}
            onClick={() => setActiveTab('batches')}
          >
            Batches ({check.batches?.length || 0})
          </button>
          <button 
            className={`tab ${activeTab === 'clarifications' ? 'active' : ''}`}
            onClick={() => setActiveTab('clarifications')}
          >
            Clarifications ({check.clarifications?.length || 0})
          </button>
        </div>

        {/* Overview Tab */}
        {activeTab === 'overview' && (
          <div className="tab-content">
            <div className="details-card">
              <div className="card-header-with-action">
                <h3 className="card-title">Check Details</h3>
                {!isEditMode ? (
                  <PermissionGuard permission={PERMISSIONS.CHECK_UPDATE}>
                    <button className="btn-primary" onClick={() => {
                      // Store original form data when entering edit mode
                      setOriginalFormData(JSON.parse(JSON.stringify(formData)));
                      setIsEditMode(true);
                    }}>
                      <svg width="16" height="16" viewBox="0 0 20 20" fill="none">
                        <path d="M11 3H5C4.46957 3 3.96086 3.21071 3.58579 3.58579C3.21071 3.96086 3 4.46957 3 5V15C3 15.5304 3.21071 16.0391 3.58579 16.4142C3.96086 16.7893 4.46957 17 5 17H15C15.5304 17 16.0391 16.7893 16.4142 16.4142C16.7893 16.0391 17 15.5304 17 15V9" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                        <path d="M14.5 1.5L18.5 5.5L11 13H7V9L14.5 1.5Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                      Edit
                    </button>
                  </PermissionGuard>
                ) : (
                  <div className="edit-actions-header">
                    <button className="btn-cancel" onClick={handleCancel}>
                      Cancel
                    </button>
                    <button className="btn-save" onClick={handleSave} disabled={isSaving}>
                      <svg width="16" height="16" viewBox="0 0 20 20" fill="none">
                        <path d="M17 3H3C2.44772 3 2 3.44772 2 4V16C2 16.5523 2.44772 17 3 17H17C17.5523 17 18 16.5523 18 16V4C18 3.44772 17.5523 3 17 3Z" stroke="currentColor" strokeWidth="2"/>
                        <path d="M6 9L9 12L14 7" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                      {isSaving ? 'Saving...' : 'Save'}
                    </button>
                  </div>
                )}
              </div>
              
              {/* Row 1: Alt. Check Number, Check Type, Batch Description (spanning 2 columns) */}
              <div className="form-grid form-row-4">
                <div className="form-group">
                  <label>Alt. Check Number</label>
                  <input 
                    type="text" 
                    value={formData.altCheckNumber || ''}
                    onChange={(e) => handleFormChange('altCheckNumber', e.target.value)}
                    disabled={!isEditMode}
                  />
                </div>
                <div className="form-group">
                  <label>Check Type</label>
                  <SearchableDropdown
                    options={checkTypes.map(ct => ({
                      value: ct.code,
                      label: ct.name
                    }))}
                    value={formData.checkType || ''}
                    onChange={(val) => handleFormChange('checkType', val)}
                    placeholder="Select Type"
                    disabled={!isEditMode || checkTypesLoading}
                    maxVisibleItems={5}
                  />
                </div>
                <div className="form-group" style={{ gridColumn: 'span 2' }}>
                  <label>Batch Description</label>
                  <input 
                    type="text" 
                    value={formData.batchDescription || ''}
                    onChange={(e) => handleFormChange('batchDescription', e.target.value)}
                    disabled={!isEditMode}
                  />
                </div>
              </div>

              {/* Row 2: Exchange, Assignee, Reporter, Workflow Status */}
              <div className="form-grid form-row-4">
                <div className="form-group">
                  <label>Exchange</label>
                  <input 
                    type="text" 
                    value={formData.exchange || ''}
                    disabled
                  />
                </div>
                <div className="form-group">
                  <label>Assignee</label>
                  <SearchableDropdown
                    options={users.map(user => ({
                      value: user.userId || user.id,
                      label: user.firstName && user.lastName
                        ? `${user.firstName} ${user.lastName}`
                        : user.email || 'Unknown User'
                    }))}
                    value={formData.assigneeId || ''}
                    onChange={(val) => handleFormChange('assigneeId', val)}
                    placeholder="Select Assignee"
                    disabled={!isEditMode}
                    maxVisibleItems={5}
                  />
                </div>
                <div className="form-group">
                  <label>Reporter</label>
                  <SearchableDropdown
                    options={users.map(user => ({
                      value: user.userId || user.id,
                      label: user.firstName && user.lastName
                        ? `${user.firstName} ${user.lastName}`
                        : user.email || 'Unknown User'
                    }))}
                    value={formData.reporterId || ''}
                    onChange={(val) => handleFormChange('reporterId', val)}
                    placeholder="Select Reporter"
                    disabled={!isEditMode}
                    maxVisibleItems={5}
                  />
                </div>
                <div className="form-group">
                  <label>Workflow Status</label>
                  {isEditMode ? (
                    <SearchableDropdown
                      options={workflowStatuses.map(ws => ({
                        value: ws.code,
                        label: ws.name
                      }))}
                      value={formData.workflowStatus || ''}
                      onChange={(val) => handleFormChange('workflowStatus', val)}
                      placeholder="Select Status"
                      disabled={workflowStatusesLoading}
                      maxVisibleItems={5}
                    />
                  ) : (
                    <input 
                      type="text" 
                      value={check?.workflowStatus ? (workflowStatuses.find(ws => ws.code === check.workflowStatus)?.name || check.workflowStatus) : 'N/A'}
                      disabled
                    />
                  )}
                </div>
              </div>

              {/* Row 3: Transfer In From, Transfer In Amount, Transfer Out From, Transfer Out Amount */}
              <div className="form-grid form-row-4">
                <div className="form-group">
                  <label>Transfer In From</label>
                  {isEditMode ? (
                    <SearchableDropdown
                      options={transferLocations.map((l) => ({
                        value: l.code,
                        label: `${l.code} - ${l.name}`,
                      }))}
                      value={formData.transferInFrom || ''}
                      onChange={(val) => handleFormChange('transferInFrom', val)}
                      placeholder="Select location..."
                      maxVisibleItems={5}
                    />
                  ) : (
                    <input 
                      type="text" 
                      value={check?.transferInFrom ? formatTransferFromDisplay(check.transferInFrom) : 'N/A'}
                      disabled
                    />
                  )}
                </div>
                <div className="form-group">
                  <label>Transfer In Amount</label>
                  {isEditMode ? (
                    <input 
                      type="number" 
                      step="0.01"
                      value={
                        formData.transferInAmount !== undefined
                          ? (formData.transferInAmount === null || formData.transferInAmount === 0 ? '' : formData.transferInAmount)
                          : (check?.transferInAmount && check.transferInAmount !== 0 ? check.transferInAmount : '')
                      }
                      onChange={(e) => {
                        const val = e.target.value;
                        if (val === '') {
                          handleFormChange('transferInAmount', null);
                        } else {
                          const numVal = parseFloat(val);
                          handleFormChange('transferInAmount', isNaN(numVal) ? null : numVal);
                        }
                      }}
                      placeholder="0"
                    />
                  ) : (
                    <input type="text" value={formatCurrency(check?.transferInAmount || 0)} disabled />
                  )}
                </div>
                <div className="form-group">
                  <label>Transfer Out From</label>
                  {isEditMode ? (
                    <SearchableDropdown
                      options={transferLocations.map((l) => ({
                        value: l.code,
                        label: `${l.code} - ${l.name}`,
                      }))}
                      value={formData.transferOutFrom || ''}
                      onChange={(val) => handleFormChange('transferOutFrom', val)}
                      placeholder="Select location..."
                      maxVisibleItems={5}
                    />
                  ) : (
                    <input 
                      type="text" 
                      value={check?.transferOutFrom ? formatTransferFromDisplay(check.transferOutFrom) : 'N/A'}
                      disabled
                    />
                  )}
                </div>
                <div className="form-group">
                  <label>Transfer Out Amount</label>
                  {isEditMode ? (
                    <input 
                      type="number" 
                      step="0.01"
                      value={
                        formData.transferOutAmount !== undefined
                          ? (formData.transferOutAmount === null || formData.transferOutAmount === 0 ? '' : formData.transferOutAmount)
                          : (check?.transferOutAmount && check.transferOutAmount !== 0 ? check.transferOutAmount : '')
                      }
                      onChange={(e) => {
                        const val = e.target.value;
                        if (val === '') {
                          handleFormChange('transferOutAmount', null);
                        } else {
                          const numVal = parseFloat(val);
                          handleFormChange('transferOutAmount', isNaN(numVal) ? null : numVal);
                        }
                      }}
                      placeholder="0"
                    />
                  ) : (
                    <input type="text" value={formatCurrency(check?.transferOutAmount || 0)} disabled />
                  )}
                </div>
              </div>

              {/* Row 4: Interest Amount, Non-AR Amount, Medical Records Fee, IOD Amount */}
              <div className="form-grid form-row-4">
                <div className="form-group">
                  <label>Interest Amount</label>
                  {isEditMode ? (
                    <input 
                      type="number" 
                      step="0.01"
                      value={
                        formData.interestAmount !== undefined 
                          ? (formData.interestAmount === 0 || formData.interestAmount === null ? '' : formData.interestAmount)
                          : (check?.interestAmount && check.interestAmount !== 0 ? check.interestAmount : '')
                      } 
                      onChange={(e) => {
                        const val = e.target.value;
                        if (val === '') {
                          handleFormChange('interestAmount', null);
                        } else {
                          const numVal = parseFloat(val);
                          handleFormChange('interestAmount', isNaN(numVal) ? null : numVal);
                        }
                      }}
                      placeholder="0"
                    />
                  ) : (
                    <input type="text" value={formatCurrency(check?.interestAmount)} disabled />
                  )}
                </div>
                <div className="form-group">
                  <label>Non-AR Amount</label>
                  {isEditMode ? (
                    <input 
                      type="number" 
                      step="0.01"
                      value={
                        formData.nonArAmount !== undefined 
                          ? (formData.nonArAmount === 0 || formData.nonArAmount === null ? '' : formData.nonArAmount)
                          : (check?.nonArAmount && check.nonArAmount !== 0 ? check.nonArAmount : '')
                      } 
                      onChange={(e) => {
                        const val = e.target.value;
                        if (val === '') {
                          handleFormChange('nonArAmount', null);
                        } else {
                          const numVal = parseFloat(val);
                          handleFormChange('nonArAmount', isNaN(numVal) ? null : numVal);
                        }
                      }}
                      placeholder="0"
                    />
                  ) : (
                    <input type="text" value={formatCurrency(check?.nonArAmount)} disabled />
                  )}
                </div>
                <div className="form-group">
                  <label>Medical Records Fee</label>
                  {isEditMode ? (
                    <input 
                      type="number" 
                      step="0.01"
                      value={
                        formData.medicalRecordsFee !== undefined 
                          ? (formData.medicalRecordsFee === 0 || formData.medicalRecordsFee === null ? '' : formData.medicalRecordsFee)
                          : (check?.medicalRecordsFee && check.medicalRecordsFee !== 0 ? check.medicalRecordsFee : '')
                      } 
                      onChange={(e) => {
                        const val = e.target.value;
                        if (val === '') {
                          handleFormChange('medicalRecordsFee', null);
                        } else {
                          const numVal = parseFloat(val);
                          handleFormChange('medicalRecordsFee', isNaN(numVal) ? null : numVal);
                        }
                      }}
                      placeholder="0"
                    />
                  ) : (
                    <input type="text" value={formatCurrency(check?.medicalRecordsFee)} disabled />
                  )}
                </div>
                <div className="form-group">
                  <label>IOD Amount</label>
                  {isEditMode ? (
                    <input 
                      type="number" 
                      step="0.01"
                      value={
                        formData.iodAmount !== undefined 
                          ? (formData.iodAmount === 0 || formData.iodAmount === null ? '' : formData.iodAmount)
                          : (check?.iodAmount && check.iodAmount !== 0 ? check.iodAmount : '')
                      } 
                      onChange={(e) => {
                        const val = e.target.value;
                        if (val === '') {
                          handleFormChange('iodAmount', null);
                        } else {
                          const numVal = parseFloat(val);
                          handleFormChange('iodAmount', isNaN(numVal) ? null : numVal);
                        }
                      }}
                      placeholder="0"
                    />
                  ) : (
                    <input type="text" value={formatCurrency(check?.iodAmount || 0)} disabled />
                  )}
                </div>
              </div>

              {/* Row 5: Pharmacy Payment Amount, Corrections Amount, miscellaneous Amount, Other Amount */}
              <div className="form-grid form-row-4">
                <div className="form-group">
                  <label>Pharmacy Payment Amount</label>
                  {isEditMode ? (
                    <input 
                      type="number" 
                      step="0.01"
                      value={
                        formData.pharmacyPaymentAmount !== undefined 
                          ? (formData.pharmacyPaymentAmount === 0 || formData.pharmacyPaymentAmount === null ? '' : formData.pharmacyPaymentAmount)
                          : (check?.pharmacyPaymentAmount && check.pharmacyPaymentAmount !== 0 ? check.pharmacyPaymentAmount : '')
                      } 
                      onChange={(e) => {
                        const val = e.target.value;
                        if (val === '') {
                          handleFormChange('pharmacyPaymentAmount', null);
                        } else {
                          const numVal = parseFloat(val);
                          handleFormChange('pharmacyPaymentAmount', isNaN(numVal) ? null : numVal);
                        }
                      }}
                      placeholder="0"
                    />
                  ) : (
                    <input type="text" value={formatCurrency(check?.pharmacyPaymentAmount || 0)} disabled />
                  )}
                </div>
                <div className="form-group">
                  <label>Corrections Amount</label>
                  {isEditMode ? (
                    <input 
                      type="number" 
                      step="0.01"
                      value={
                        formData.correctionsAmount !== undefined 
                          ? (formData.correctionsAmount === 0 || formData.correctionsAmount === null ? '' : formData.correctionsAmount)
                          : (check?.correctionsAmount && check.correctionsAmount !== 0 ? check.correctionsAmount : '')
                      } 
                      onChange={(e) => {
                        const val = e.target.value;
                        if (val === '') {
                          handleFormChange('correctionsAmount', null);
                        } else {
                          const numVal = parseFloat(val);
                          handleFormChange('correctionsAmount', isNaN(numVal) ? null : numVal);
                        }
                      }}
                      placeholder="0"
                    />
                  ) : (
                    <input type="text" value={formatCurrency(check?.correctionsAmount || 0)} disabled />
                  )}
                </div>
                <div className="form-group">
                  <label>miscellaneous Amount</label>
                  {isEditMode ? (
                    <input 
                      type="number" 
                      step="0.01"
                      value={
                        formData.miscPaymentAmount !== undefined 
                          ? (formData.miscPaymentAmount === 0 || formData.miscPaymentAmount === null ? '' : formData.miscPaymentAmount)
                          : (check?.miscPaymentAmount && check.miscPaymentAmount !== 0 ? check.miscPaymentAmount : '')
                      } 
                      onChange={(e) => {
                        const val = e.target.value;
                        if (val === '') {
                          handleFormChange('miscPaymentAmount', null);
                        } else {
                          const numVal = parseFloat(val);
                          handleFormChange('miscPaymentAmount', isNaN(numVal) ? null : numVal);
                        }
                      }}
                      placeholder="0"
                    />
                  ) : (
                    <input type="text" value={formatCurrency(check?.miscPaymentAmount || 0)} disabled />
                  )}
                </div>
                <div className="form-group">
                  <label>Other Amount</label>
                  {isEditMode ? (
                    <input 
                      type="number" 
                      step="0.01"
                      value={
                        formData.otherAmount !== undefined
                          ? (formData.otherAmount === null || formData.otherAmount === 0 ? '' : formData.otherAmount)
                          : (check?.otherAmount && check.otherAmount !== 0 ? check.otherAmount : '')
                      }
                      onChange={(e) => {
                        const val = e.target.value;
                        if (val === '') {
                          handleFormChange('otherAmount', null);
                        } else {
                          const numVal = parseFloat(val);
                          handleFormChange('otherAmount', isNaN(numVal) ? null : numVal);
                        }
                      }}
                      placeholder="0"
                    />
                  ) : (
                    <input type="text" value={formatCurrency(check?.otherAmount || 0)} disabled />
                  )}
                </div>
              </div>

              {/* Row 6: Unknown Account Number (one column of four) */}
              <div className="form-grid form-row-4">
                <div className="form-group">
                  <label>Unknown Account Number</label>
                  <input
                    type="text"
                    value={
                      check?.unknownAccountNumber != null && String(check.unknownAccountNumber).trim() !== ''
                        ? String(check.unknownAccountNumber)
                        : 'N/A'
                    }
                    disabled
                  />
                </div>
              </div>

              {/* Row 7: Legacy Notes */}
              <div className="form-grid form-row-1">
                <div className="form-group">
                  <label>Legacy Notes</label>
                  <textarea 
                    value={formData.legacyNotes || ''}
                    onChange={(e) => handleFormChange('legacyNotes', e.target.value)}
                    disabled={!isEditMode}
                    rows="1"
                  />
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Batches Tab */}
        {activeTab === 'batches' && (
          <div className="tab-content">
            <div className="batches-card">
              <div className="card-header">
                <h3 className="card-title">Batches</h3>
                {batchModalMode !== 'add' && !editingBatchId && (
                  <PermissionGuard permission={PERMISSIONS.CHECK_BATCH_CREATE}>
                    <button className="btn-primary" onClick={() => {
                      setBatchFormData({
                        batchRunNumber: '',
                        batchNumber: '',
                        batchDate: '',
                        batchType: 'MANUAL',
                        batchSource: 'Athena',
                        batchAmount: '',
                        batchNotes: '',
                        invoiceNumber: '',
                        edmNumber: '',
                        correctionBatch: false
                      });
                      setViewingBatchId('new'); // Use 'new' as a special ID for add mode
                      setBatchModalMode('add');
                      setBatchValidationErrors({});
                    }}>
                      <svg width="16" height="16" viewBox="0 0 20 20" fill="none">
                        <path d="M10 3V17M3 10H17" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                      </svg>
                      Add Batch
                    </button>
                  </PermissionGuard>
                )}
              </div>


              {/* Batches Table */}
              <div className="batches-table-wrapper">
                <table className="batches-table">
                  <thead>
                    <tr>
                      <th className="sortable" onClick={() => handleSortBatches('batchType')}>
                        Batch Type {getSortArrow('batchType')}
                      </th>
                      <th className="sortable" onClick={() => handleSortBatches('batchNumber')}>
                        Batch Number {getSortArrow('batchNumber')}
                      </th>
                      <th className="sortable" onClick={() => handleSortBatches('batchRunNumber')}>
                        Run Number {getSortArrow('batchRunNumber')}
                      </th>
                      <th>Notes</th>
                      <th className="sortable" onClick={() => handleSortBatches('edmNumber')}>
                        EDM Number {getSortArrow('edmNumber')}
                      </th>
                      <th className="sortable" onClick={() => handleSortBatches('batchAmount')}>
                        Amount {getSortArrow('batchAmount')}
                      </th>
                      <th>Correction Batch</th>
                      <th className="sortable" onClick={() => handleSortBatches('updatedAt')}>
                        Updated At {getSortArrow('updatedAt')}
                      </th>
                      {check.checkType === 'DIT_DRL' && <th>DRL Allocation</th>}
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {check.batches && check.batches.length > 0 ? (
                      getSortedBatches().map((batch) => (
                        editingBatchId === batch.batchId ? (
                          <React.Fragment key={batch.batchId}>
                            <tr className="editing-row">
                              <td>{batchFormData.batchType || 'N/A'}</td>
                              <td><TruncatedText text={batchFormData.batchNumber} maxLength={15} /></td>
                              <td><TruncatedText text={batchFormData.batchRunNumber} maxLength={15} /></td>
                              <td><TruncatedText text={batchFormData.batchNotes} maxLength={15} /></td>
                              <td><TruncatedText text={batchFormData.edmNumber} maxLength={15} /></td>
                              <td>{formatCurrency(batchFormData.batchAmount || 0)}</td>
                              <td>
                                <span className={`batch-correction-badge ${batchFormData.correctionBatch ? 'correction-yes' : 'correction-no'}`}>
                                  {batchFormData.correctionBatch ? 'Yes' : 'No'}
                                </span>
                              </td>
                              <td>
                                <UserTimestamp
                                  userId={batch.updatedBy}
                                  dateTime={batch.updatedAt}
                                  action="updated"
                                  getUserName={getUserName}
                                  getUserById={getUserById}
                                />
                              </td>
                              {check.checkType === 'DIT_DRL' && (
                                <td>
                                  <div className="drl-allocation-cell" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                    <span style={{ 
                                      display: 'inline-flex',
                                      alignItems: 'center',
                                      justifyContent: 'center',
                                      padding: '6px 12px',
                                      backgroundColor: '#d1fae5',
                                      borderRadius: '6px',
                                      fontSize: '13px',
                                      fontWeight: '600',
                                      color: '#065f46',
                                      minWidth: '40px'
                                    }}>
                                      {getDrlAllocationCount(batch)}
                                    </span>
                                  </div>
                                </td>
                              )}
                              <td>
                                <div className="inline-actions">
                                  <button className="btn-icon save" onClick={(e) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    handleUpdateBatch();
                                  }} title="Save" disabled={isUpdatingBatch}>
                                    <svg width="16" height="16" viewBox="0 0 20 20" fill="none">
                                      <path d="M6 9L9 12L14 7" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                    </svg>
                                  </button>
                                  <button className="btn-icon cancel" onClick={(e) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    handleCancelBatch();
                                  }} title="Cancel">
                                    <svg width="16" height="16" viewBox="0 0 20 20" fill="none">
                                      <path d="M5 5L15 15M15 5L5 15" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                                    </svg>
                                  </button>
                                </div>
                              </td>
                            </tr>
                          </React.Fragment>
                        ) : (
                          <React.Fragment key={batch.batchId}>
                            <tr 
                              data-batch-id={batch.batchId} 
                              className={`${batch.isArchived ? 'batch-archived' : ''} ${batch.invoiceNumber ? 'batch-unknown-indicator' : ''}`}
                            >
                              <td>{batch.batchType || 'N/A'}</td>
                              <td><TruncatedText text={batch.batchNumber} maxLength={15} /></td>
                              <td><TruncatedText text={batch.batchRunNumber} maxLength={15} /></td>
                              <td><TruncatedText text={batch.batchNotes} maxLength={15} /></td>
                              <td><TruncatedText text={batch.edmNumber} maxLength={15} /></td>
                              <td>{formatCurrency(batch.batchAmount)}</td>
                              <td>
                                <span className={`batch-correction-badge ${batch.isCorrectionBatch ? 'correction-yes' : 'correction-no'}`}>
                                  {batch.isCorrectionBatch ? 'Yes' : 'No'}
                                </span>
                              </td>
                              <td>
                                <UserTimestamp
                                  userId={batch.updatedBy}
                                  dateTime={batch.updatedAt}
                                  action="updated"
                                  getUserName={getUserName}
                                  getUserById={getUserById}
                                />
                              </td>
                              {check.checkType === 'DIT_DRL' && (
                                <td onClick={(e) => e.stopPropagation()}>
                                  <div className="drl-allocation-cell" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                    <button
                                      onClick={(e) => {
                                        e.preventDefault();
                                        e.stopPropagation();
                                        setSelectedBatchForDrlAllocation(batch);
                                        setShowDrlAllocationModal(true);
                                      }}
                                      style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        padding: '6px 12px',
                                        backgroundColor: '#d1fae5',
                                        borderRadius: '6px',
                                        fontSize: '13px',
                                        fontWeight: '600',
                                        color: '#065f46',
                                        border: 'none',
                                        cursor: 'pointer',
                                        transition: 'all 0.2s',
                                        minWidth: '40px'
                                      }}
                                      onMouseEnter={(e) => {
                                        e.target.style.backgroundColor = '#10b981';
                                        e.target.style.color = 'white';
                                        e.target.style.transform = 'scale(1.05)';
                                        e.target.style.boxShadow = '0 2px 4px rgba(0, 0, 0, 0.1)';
                                      }}
                                      onMouseLeave={(e) => {
                                        e.target.style.backgroundColor = '#d1fae5';
                                        e.target.style.color = '#065f46';
                                        e.target.style.transform = 'scale(1)';
                                        e.target.style.boxShadow = 'none';
                                      }}
                                      title={`${getDrlAllocationCount(batch)} DRL allocation(s) - Click to view details`}
                                    >
                                      {getDrlAllocationCount(batch)}
                                    </button>
                                  </div>
                                </td>
                              )}
                              <td>
                                <div className="inline-actions">
                                  <button 
                                    className="btn-icon view"
                                    onClick={() => {
                                      setViewingBatchId(batch.batchId);
                                      setBatchModalMode('view');
                                    }}
                                    title="View Details"
                                  >
                                    <svg width="16" height="16" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                      <path d="M1 10s4-6 9-6 9 6 9 6-4 6-9 6-9-6-9-6z"/>
                                      <circle cx="10" cy="10" r="3"/>
                                    </svg>
                                  </button>
                                  <PermissionGuard permission={PERMISSIONS.CHECK_BATCH_UPDATE}>
                                    <button 
                                      className="btn-icon edit"
                                      onClick={() => {
                                        handleEditBatch(batch);
                                        setViewingBatchId(batch.batchId);
                                        setBatchModalMode('edit');
                                      }}
                                      title="Edit"
                                    >
                                      <svg width="16" height="16" viewBox="0 0 20 20" fill="none">
                                        <path d="M11 3H5C4.46957 3 3.96086 3.21071 3.58579 3.58579C3.21071 3.96086 3 4.46957 3 5V15C3 15.5304 3.21071 16.0391 3.58579 16.4142C3.96086 16.7893 4.46957 17 5 17H15C15.5304 17 16.0391 16.7893 16.4142 16.4142C16.7893 16.0391 17 15.5304 17 15V9" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                                        <path d="M14.5 1.5L18.5 5.5L11 13H7V9L14.5 1.5Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                      </svg>
                                    </button>
                                  </PermissionGuard>
                                  <PermissionGuard permission={PERMISSIONS.CHECK_BATCH_UPDATE}>
                                    <button 
                                      className={`btn-icon archive ${batch.isArchived ? 'archived' : ''}`}
                                      onClick={() => handleArchiveBatchClick(batch.batchId, batch.isArchived)}
                                      title={batch.isArchived ? 'Unarchive' : 'Archive'}
                                    >
                                      <svg width="16" height="16" viewBox="0 0 20 20" fill="none">
                                        {batch.isArchived ? (
                                          <path d="M3 4H17M4 4V17C4 17.5304 4.21071 18.0391 4.58579 18.4142C4.96086 18.7893 5.46957 19 6 19H14C14.5304 19 15.0391 18.7893 15.4142 18.4142C15.7893 18.0391 16 17.5304 16 17V4M7 8L10 11L13 8" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                        ) : (
                                          <path d="M3 4H17M4 4V17C4 17.5304 4.21071 18.0391 4.58579 18.4142C4.96086 18.7893 5.46957 19 6 19H14C14.5304 19 15.0391 18.7893 15.4142 18.4142C15.7893 18.0391 16 17.5304 16 17V4M7 8L10 5L13 8" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                        )}
                                      </svg>
                                    </button>
                                  </PermissionGuard>
                                  <PermissionGuard permission={PERMISSIONS.CHECK_BATCH_DELETE}>
                                    <button 
                                      className="btn-icon delete"
                                      onClick={() => handleDeleteBatch(batch.batchId, batch.isArchived)}
                                      title="Delete"
                                      style={{ color: '#dc2626' }}
                                    >
                                      <svg width="16" height="16" viewBox="0 0 20 20" fill="none">
                                        <path d="M3 6H5H17M8 6V4C8 3.46957 8.21071 2.96086 8.58579 2.58579C8.96086 2.21071 9.46957 2 10 2C10.5304 2 11.0391 2.21071 11.4142 2.58579C11.7893 2.96086 12 3.46957 12 4V6M15 6V16C15 16.5304 14.7893 17.0391 14.4142 17.4142C14.0391 17.7893 13.5304 18 13 18H7C6.46957 18 5.96086 17.7893 5.58579 17.4142C5.21071 17.0391 5 16.5304 5 16V6H15Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                      </svg>
                                    </button>
                                  </PermissionGuard>
                                </div>
                              </td>
                            </tr>
                          </React.Fragment>
                        )
                      ))
                    ) : (
                      <tr>
                        <td colSpan={10} className="empty-state">No batches found</td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}

        {/* Clarifications Tab */}
        {activeTab === 'clarifications' && (
          <div className="tab-content">
            <div className="clarifications-card">
              <div className="card-header">
                <h3 className="card-title">Clarifications</h3>
                {!isAddingClarification && !editingClarificationId && (
                  <PermissionGuard permission={PERMISSIONS.CHECK_CLARIFICATION_CREATE}>
                    <button className="btn-primary" onClick={() => setIsAddingClarification(true)}>
                      <svg width="16" height="16" viewBox="0 0 20 20" fill="none">
                        <path d="M10 3V17M3 10H17" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                      </svg>
                      Add Clarification
                    </button>
                  </PermissionGuard>
                )}
              </div>

              {/* Add Clarification Form */}
              {isAddingClarification && (
                <div className="clarification-form-card">
                  <h4>Add New Clarification</h4>
                  {/* Row 1: Clarification Type, Assignee, Reporter */}
                  <div className="form-grid form-row-3">
                    <div className="form-group">
                      <label>Clarification Type *</label>
                      <SearchableDropdown
                        options={[
                          { value: '', label: 'Select Clarification Type' },
                          ...clarificationTypes.map(ct => ({
                            value: ct.code,
                            label: ct.name
                          }))
                        ]}
                        value={clarificationFormData.clarificationType}
                        onChange={(value) => handleClarificationFormChange('clarificationType', value)}
                        placeholder="Select Clarification Type"
                        maxVisibleItems={5}
                        disabled={clarificationTypesLoading}
                      />
                    </div>
                    <div className="form-group">
                      <label>Assignee</label>
                      <select 
                        value={clarificationFormData.assignee || 'ON-SHORE'}
                        onChange={(e) => handleClarificationFormChange('assignee', e.target.value)}
                      >
                        <option value="ON-SHORE">ON-SHORE</option>
                        <option value="OFF-SHORE">OFF-SHORE</option>
                      </select>
                    </div>
                    <div className="form-group">
                      <label>Reporter</label>
                      <select 
                        value={clarificationFormData.reportee || 'OFF-SHORE'}
                        onChange={(e) => handleClarificationFormChange('reportee', e.target.value)}
                      >
                        <option value="ON-SHORE">ON-SHORE</option>
                        <option value="OFF-SHORE">OFF-SHORE</option>
                      </select>
                    </div>
                  </div>
                  {/* Row 2: Status, Amount, Assignee Name, Reporter Name */}
                  <div className="form-grid form-row-4">
                    <div className="form-group">
                      <label>Status</label>
                      <select 
                        value={clarificationFormData.status}
                        onChange={(e) => handleClarificationFormChange('status', e.target.value)}
                      >
                        <option value="OPEN">Open</option>
                        <option value="RESOLVED">Resolved</option>
                      </select>
                    </div>
                    <div className="form-group">
                      <label>Amount (Optional)</label>
                      <input 
                        type="number" 
                        step="0.01"
                        value={clarificationFormData.amount || ''}
                        onChange={(e) => handleClarificationFormChange('amount', e.target.value)}
                        placeholder="Enter amount"
                      />
                    </div>
                    <div className="form-group">
                      <label>Assignee Name</label>
                      <SearchableDropdown
                        options={[
                          { value: '', label: 'Select Assignee' },
                          ...(users || []).map(user => ({
                            value: user.userId || user.id,
                            label: user.firstName && user.lastName 
                              ? `${user.firstName} ${user.lastName}` 
                              : user.email || 'Unknown User'
                          }))
                        ]}
                        value={clarificationFormData.assigneeId || ''}
                        onChange={(value) => handleClarificationFormChange('assigneeId', value)}
                        placeholder="Select Assignee"
                        maxVisibleItems={5}
                      />
                    </div>
                    <div className="form-group">
                      <label>Reporter Name</label>
                      <SearchableDropdown
                        options={[
                          { value: '', label: 'Select Reporter' },
                          ...(users || []).map(user => ({
                            value: user.userId || user.id,
                            label: user.firstName && user.lastName 
                              ? `${user.firstName} ${user.lastName}` 
                              : user.email || 'Unknown User'
                          }))
                        ]}
                        value={clarificationFormData.reporterId || ''}
                        onChange={(value) => handleClarificationFormChange('reporterId', value)}
                        placeholder="Select Reporter"
                        maxVisibleItems={5}
                      />
                    </div>
                  </div>
                  {/* Row: Page Number, Patient Name, Patient Account Number, Insurance */}
                  <div className="form-grid form-row-4">
                    <div className="form-group">
                      <label>Page Number</label>
                      <input
                        type="text"
                        value={clarificationFormData.pageNumber || ''}
                        onChange={(e) => handleClarificationFormChange('pageNumber', e.target.value)}
                        placeholder="Page number"
                      />
                    </div>
                    <div className="form-group">
                      <label>Patient Name</label>
                      <input
                        type="text"
                        value={clarificationFormData.patientName || ''}
                        onChange={(e) => handleClarificationFormChange('patientName', e.target.value)}
                        placeholder="Patient name"
                      />
                    </div>
                    <div className="form-group">
                      <label>Patient Account Number</label>
                      <input
                        type="text"
                        value={clarificationFormData.patientAccountNumber || ''}
                        onChange={(e) => handleClarificationFormChange('patientAccountNumber', e.target.value)}
                        placeholder="Patient account number"
                      />
                    </div>
                    <div className="form-group">
                      <label>Insurance</label>
                      <input
                        type="text"
                        value={clarificationFormData.insurance || ''}
                        onChange={(e) => handleClarificationFormChange('insurance', e.target.value)}
                        placeholder="Insurance"
                      />
                    </div>
                  </div>
                  <div className="form-grid form-row-1">
                    <div className="form-group full-width textarea-with-expand">
                      <label>Details</label>
                      <div className="textarea-wrapper">
                        <textarea 
                          value={clarificationFormData.details}
                          onChange={(e) => handleClarificationFormChange('details', e.target.value)}
                          rows={expandedTextareas['create-details'] ? 4 : 1}
                          placeholder="Enter clarification details"
                          className={expandedTextareas['create-details'] ? '' : 'textarea-single-row'}
                        />
                        <button
                          type="button"
                          className="textarea-expand-btn"
                          onClick={() => setExpandedTextareas(prev => ({
                            ...prev,
                            'create-details': !prev['create-details']
                          }))}
                          title={expandedTextareas['create-details'] ? 'Collapse' : 'Expand'}
                        >
                          <svg width="16" height="16" viewBox="0 0 20 20" fill="none">
                            {expandedTextareas['create-details'] ? (
                              <path d="M5 12.5L10 7.5L15 12.5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                            ) : (
                              <path d="M5 7.5L10 12.5L15 7.5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                            )}
                          </svg>
                        </button>
                      </div>
                    </div>
                  </div>
                  <div className="form-actions">
                    <button className="btn-cancel" onClick={handleCancelClarification}>Cancel</button>
                    <button className="btn-save" onClick={handleAddClarification} disabled={isCreatingClarification}>
                      {isCreatingClarification ? 'Creating...' : 'Save Clarification'}
                    </button>
                  </div>
                </div>
              )}

              {/* Clarifications List */}
              {check?.clarifications && check.clarifications.length > 0 ? (
                <div className="clarifications-list-redesigned">
                  {check.clarifications.map((clarification) => (
                    <div 
                      key={clarification.clarificationId}
                      className={`clarification-item ${editingClarificationId === clarification.clarificationId ? 'editing' : ''}`}
                      data-clarification-id={clarification.clarificationId}
                    >
                      {editingClarificationId === clarification.clarificationId ? (
                        // Edit Mode
                        <div className="clarification-edit-form">
                          <div className="clarification-edit-heading">
                            <h4>Edit Clarification</h4>
                            {clarification.clarificationId && (
                              <span className="clarification-id-with-copy">
                                <span className="field-label-inline">Clarification ID:</span>
                                <button
                                  type="button"
                                  className="btn-icon clarification-id-copy-btn"
                                  onClick={() => handleCopyClarificationId(clarification.clarificationId)}
                                  title="Copy clarification ID"
                                  aria-label="Copy clarification ID"
                                >
                                  <Copy size={16} aria-hidden />
                                </button>
                                <span className="field-value-inline clarification-id-text">{clarification.clarificationId}</span>
                              </span>
                            )}
                          </div>
                          {/* Row 1: Clarification Type, Assignee, Reporter */}
                          <div className="form-grid form-row-3">
                            <div className="form-group">
                              <label>Clarification Type *</label>
                              <SearchableDropdown
                                options={[
                                  { value: '', label: 'Select Clarification Type' },
                                  ...clarificationTypes.map(ct => ({
                                    value: ct.code,
                                    label: ct.name
                                  }))
                                ]}
                                value={clarificationFormData.clarificationType}
                                onChange={(value) => handleClarificationFormChange('clarificationType', value)}
                                placeholder="Select Clarification Type"
                                maxVisibleItems={5}
                                disabled={clarificationTypesLoading}
                              />
                            </div>
                            <div className="form-group">
                              <label>Assignee</label>
                              <select 
                                value={clarificationFormData.assignee || 'ON-SHORE'}
                                onChange={(e) => handleClarificationFormChange('assignee', e.target.value)}
                              >
                                <option value="ON-SHORE">ON-SHORE</option>
                                <option value="OFF-SHORE">OFF-SHORE</option>
                              </select>
                            </div>
                            <div className="form-group">
                              <label>Reporter</label>
                              <select 
                                value={clarificationFormData.reportee || 'OFF-SHORE'}
                                onChange={(e) => handleClarificationFormChange('reportee', e.target.value)}
                              >
                                <option value="ON-SHORE">ON-SHORE</option>
                                <option value="OFF-SHORE">OFF-SHORE</option>
                              </select>
                            </div>
                          </div>
                          {/* Row 2: Status, Amount, Assignee Name, Reporter Name */}
                          <div className="form-grid form-row-4">
                            <div className="form-group">
                              <label>Status</label>
                              <select 
                                value={clarificationFormData.status}
                                onChange={(e) => handleClarificationFormChange('status', e.target.value)}
                              >
                                <option value="OPEN">Open</option>
                                <option value="RESOLVED">Resolved</option>
                              </select>
                            </div>
                            <div className="form-group">
                              <label>Amount (Optional)</label>
                              <input 
                                type="number" 
                                step="0.01"
                                value={clarificationFormData.amount || ''}
                                onChange={(e) => handleClarificationFormChange('amount', e.target.value)}
                                placeholder="Enter amount"
                              />
                            </div>
                            <div className="form-group">
                              <label>Assignee Name</label>
                              <SearchableDropdown
                                options={[
                                  { value: '', label: 'Select Assignee' },
                                  ...(users || []).map(user => ({
                                    value: user.userId || user.id,
                                    label: user.firstName && user.lastName 
                                      ? `${user.firstName} ${user.lastName}` 
                                      : user.email || 'Unknown User'
                                  }))
                                ]}
                                value={clarificationFormData.assigneeId || ''}
                                onChange={(value) => handleClarificationFormChange('assigneeId', value)}
                                placeholder="Select Assignee"
                                maxVisibleItems={5}
                              />
                            </div>
                            <div className="form-group">
                              <label>Reporter Name</label>
                              <SearchableDropdown
                                options={[
                                  { value: '', label: 'Select Reporter' },
                                  ...(users || []).map(user => ({
                                    value: user.userId || user.id,
                                    label: user.firstName && user.lastName 
                                      ? `${user.firstName} ${user.lastName}` 
                                      : user.email || 'Unknown User'
                                  }))
                                ]}
                                value={clarificationFormData.reporterId || ''}
                                onChange={(value) => handleClarificationFormChange('reporterId', value)}
                                placeholder="Select Reporter"
                                maxVisibleItems={5}
                              />
                            </div>
                          </div>
                          {/* Row: Page Number, Patient Name, Patient Account Number, Insurance */}
                          <div className="form-grid form-row-4">
                            <div className="form-group">
                              <label>Page Number</label>
                              <input
                                type="text"
                                value={clarificationFormData.pageNumber || ''}
                                onChange={(e) => handleClarificationFormChange('pageNumber', e.target.value)}
                                placeholder="Page number"
                              />
                            </div>
                            <div className="form-group">
                              <label>Patient Name</label>
                              <input
                                type="text"
                                value={clarificationFormData.patientName || ''}
                                onChange={(e) => handleClarificationFormChange('patientName', e.target.value)}
                                placeholder="Patient name"
                              />
                            </div>
                            <div className="form-group">
                              <label>Patient Account Number</label>
                              <input
                                type="text"
                                value={clarificationFormData.patientAccountNumber || ''}
                                onChange={(e) => handleClarificationFormChange('patientAccountNumber', e.target.value)}
                                placeholder="Patient account number"
                              />
                            </div>
                            <div className="form-group">
                              <label>Insurance</label>
                              <input
                                type="text"
                                value={clarificationFormData.insurance || ''}
                                onChange={(e) => handleClarificationFormChange('insurance', e.target.value)}
                                placeholder="Insurance"
                              />
                            </div>
                          </div>
                          <div className="form-grid form-row-1">
                            <div className="form-group full-width textarea-with-expand">
                              <label>Details</label>
                              <div className="textarea-wrapper">
                                <textarea 
                                  value={clarificationFormData.details}
                                  onChange={(e) => handleClarificationFormChange('details', e.target.value)}
                                  rows={expandedTextareas[`edit-details-${editingClarificationId}`] ? 4 : 1}
                                  className={expandedTextareas[`edit-details-${editingClarificationId}`] ? '' : 'textarea-single-row'}
                                />
                                <button
                                  type="button"
                                  className="textarea-expand-btn"
                                  onClick={() => setExpandedTextareas(prev => ({
                                    ...prev,
                                    [`edit-details-${editingClarificationId}`]: !prev[`edit-details-${editingClarificationId}`]
                                  }))}
                                  title={expandedTextareas[`edit-details-${editingClarificationId}`] ? 'Collapse' : 'Expand'}
                                >
                                  <svg width="16" height="16" viewBox="0 0 20 20" fill="none">
                                    {expandedTextareas[`edit-details-${editingClarificationId}`] ? (
                                      <path d="M5 12.5L10 7.5L15 12.5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                    ) : (
                                      <path d="M5 7.5L10 12.5L15 7.5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                    )}
                                  </svg>
                                </button>
                              </div>
                            </div>
                          </div>
                          <div className="form-actions">
                            <button className="btn-cancel" onClick={handleCancelClarification}>Cancel</button>
                            <button className="btn-save" onClick={handleUpdateClarification} disabled={isUpdatingClarification}>
                              {isUpdatingClarification ? 'Updating...' : 'Update'}
                            </button>
                          </div>
                        </div>
                      ) : (
                        // View Mode
                        <>
                          {/* Row 1: Type (before value), Status, Assignee, Reporter, Opened, Actions */}
                          <div className="clarification-row-1">
                            <div className="clarification-main-info">
                              {clarification.clarificationId && (
                                <span className="clarification-field-inline clarification-id-inline">
                                  <span className="field-label-inline">Clarification ID:</span>
                                  <button
                                    type="button"
                                    className="btn-icon clarification-id-copy-btn"
                                    onClick={() => handleCopyClarificationId(clarification.clarificationId)}
                                    title="Copy clarification ID"
                                    aria-label="Copy clarification ID"
                                  >
                                    <Copy size={16} aria-hidden />
                                  </button>
                                  <span className="field-value-inline clarification-id-text">{clarification.clarificationId}</span>
                                </span>
                              )}
                              <span className="clarification-type-label">Type:</span>
                              <span className="clarification-type">
                                {(() => {
                                  if (!clarification.clarificationType) return 'N/A';
                                  const ctItem = clarificationTypes.find(ct => ct.code === clarification.clarificationType);
                                  return ctItem?.name || clarification.clarificationType;
                                })()}
                              </span>
                              <span className={`clarification-status ${clarification.status?.toLowerCase()}`}>
                                {clarification.status || 'OPEN'}
                              </span>
                              <span className="clarification-field-inline">
                                <span className="field-label-inline">Amount:</span>
                                <span className="field-value-inline">{clarification.amount !== undefined && clarification.amount !== null ? clarification.amount : 0}</span>
                              </span>
                              <span className="clarification-field-inline">
                                <span className="field-label-inline">Assignee:</span>
                                <span className="field-value-inline">{clarification.assignee || 'N/A'}</span>
                              </span>
                              <span className="clarification-field-inline">
                                <span className="field-label-inline">Reporter:</span>
                                <span className="field-value-inline">{clarification.reportee || 'N/A'}</span>
                              </span>
                              <span className="clarification-field-inline">
                                <span className="field-label-inline">Assignee Name:</span>
                                <span className="field-value-inline">{getUserName(clarification.assigneeId) || 'N/A'}</span>
                              </span>
                              <span className="clarification-field-inline">
                                <span className="field-label-inline">Reporter Name:</span>
                                <span className="field-value-inline">{getUserName(clarification.reporterId) || 'N/A'}</span>
                              </span>
                              {clarification.pageNumber != null && clarification.pageNumber !== '' && (
                                <span className="clarification-field-inline">
                                  <span className="field-label-inline">Page #:</span>
                                  <span className="field-value-inline">{clarification.pageNumber}</span>
                                </span>
                              )}
                              {clarification.patientName != null && clarification.patientName !== '' && (
                                <span className="clarification-field-inline">
                                  <span className="field-label-inline">Patient Name:</span>
                                  <span className="field-value-inline">{clarification.patientName}</span>
                                </span>
                              )}
                              {clarification.patientAccountNumber != null && clarification.patientAccountNumber !== '' && (
                                <span className="clarification-field-inline">
                                  <span className="field-label-inline">Patient Acct #:</span>
                                  <span className="field-value-inline">{clarification.patientAccountNumber}</span>
                                </span>
                              )}
                              {clarification.insurance != null && clarification.insurance !== '' && (
                                <span className="clarification-field-inline">
                                  <span className="field-label-inline">Insurance:</span>
                                  <span className="field-value-inline">{clarification.insurance}</span>
                                </span>
                              )}
                              <span className="clarification-field-inline">
                                <span className="field-label-inline">Opened:</span>
                                <span className="field-value-inline">{clarification.openedAt ? formatDateUS(clarification.openedAt.split('T')[0]) : 'N/A'}</span>
                              </span>
                              {clarification.resolvedAt && (
                                <span className="clarification-field-inline">
                                  <span className="field-label-inline">Resolved:</span>
                                  <span className="field-value-inline">{formatDateUS(clarification.resolvedAt.split('T')[0])}</span>
                                </span>
                              )}
                            </div>
                            <div className="clarification-actions">
                              <PermissionGuard permission={PERMISSIONS.CHECK_CLARIFICATION_UPDATE}>
                                <button 
                                  className="btn-icon edit"
                                  onClick={() => handleEditClarification(clarification)}
                                  title="Edit"
                                >
                                  <svg width="16" height="16" viewBox="0 0 20 20" fill="none">
                                    <path d="M11 3H5C4.46957 3 3.96086 3.21071 3.58579 3.58579C3.21071 3.96086 3 4.46957 3 5V15C3 15.5304 3.21071 16.0391 3.58579 16.4142C3.96086 16.7893 4.46957 17 5 17H15C15.5304 17 16.0391 16.7893 16.4142 16.4142C16.7893 16.0391 17 15.5304 17 15V9" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                                    <path d="M14.5 1.5L18.5 5.5L11 13H7V9L14.5 1.5Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                  </svg>
                                </button>
                              </PermissionGuard>
                            </div>
                          </div>

                          {/* Row 2: Details */}
                          <div className="clarification-row-2">
                            <div className="clarification-details-full">
                              <span className="field-label-inline">Details:</span>
                              <span className="field-value-inline">{clarification.details || 'No details provided'}</span>
                            </div>
                          </div>

                          {/* Row 3: Comments Section (minimized by default) */}
                          <div className="clarification-row-3">
                            <button 
                              className="comments-toggle-btn-below"
                              onClick={() => toggleCommentsExpanded(clarification.clarificationId)}
                              title={expandedCommentsClarificationId === clarification.clarificationId ? 'Hide comments' : 'Show comments'}
                            >
                              <span className="comments-label">Comments</span>
                              <span className="comments-count">({clarification.comments?.length || 0})</span>
                              <svg 
                                width="16" 
                                height="16" 
                                viewBox="0 0 20 20" 
                                fill="none"
                                className={`comments-arrow ${expandedCommentsClarificationId === clarification.clarificationId ? 'expanded' : ''}`}
                              >
                                <path d="M5 7.5L10 12.5L15 7.5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                              </svg>
                            </button>
                            
                            {expandedCommentsClarificationId === clarification.clarificationId && (
                              <div className="comments-section-expanded">
                                <div className="comments-list">
                                  {clarification.comments && clarification.comments.length > 0 ? (
                                    clarification.comments.map((comment) => (
                                      <div key={comment.commentId} className="comment-item-message">
                                        <div className="comment-header">
                                          <span className="comment-author">{comment.userDisplayName || 'Unknown'}</span>
                                          <span className="comment-date">{formatDateTime(comment.commentedAt)}</span>
                                        </div>
                                        <div className="comment-body">
                                          <span className="comment-text">{comment.comment}</span>
                                        </div>
                                      </div>
                                    ))
                                  ) : (
                                    <div className="comments-empty-state">No comments yet</div>
                                  )}
                                </div>
                                
                                {/* Add Comment Form */}
                                <div className="add-comment-form">
                                  <div className="comment-form-grid">
                                    <div className="comment-form-row comment-textarea-row">
                                      <textarea 
                                        placeholder="Add a comment..."
                                        value={commentText[clarification.clarificationId] || ''}
                                        onChange={(e) => {
                                          const filteredValue = filterEmojis(e.target.value);
                                          setCommentText(prev => ({
                                            ...prev,
                                            [clarification.clarificationId]: filteredValue
                                          }));
                                          // Auto-resize textarea only if it has content
                                          if (filteredValue.trim()) {
                                            e.target.style.height = 'auto';
                                            e.target.style.height = Math.max(36, e.target.scrollHeight) + 'px';
                                          } else {
                                            e.target.style.height = '36px';
                                          }
                                        }}
                                        onFocus={(e) => {
                                          // Allow expansion on focus if there's content
                                          if (e.target.value.trim()) {
                                            e.target.style.height = 'auto';
                                            e.target.style.height = Math.max(36, e.target.scrollHeight) + 'px';
                                          }
                                        }}
                                        onBlur={(e) => {
                                          // Collapse to single row if empty
                                          if (!e.target.value.trim()) {
                                            e.target.style.height = '36px';
                                          }
                                        }}
                                        rows="1"
                                        className="comment-textarea-single-line"
                                      />
                                      <div className="comment-assignee-select">
                                        <label className="comment-assignee-label">Assignee</label>
                                        <select 
                                          value={commentAssignee[clarification.clarificationId] !== undefined 
                                            ? (commentAssignee[clarification.clarificationId] || '') 
                                            : (clarification.assignee || '')}
                                          onChange={(e) => {
                                            const selectedValue = e.target.value || undefined;
                                            setCommentAssignee(prev => ({
                                              ...prev,
                                              [clarification.clarificationId]: selectedValue
                                            }));
                                            // Auto-set reporter based on assignee
                                            if (selectedValue === 'ON-SHORE') {
                                              setCommentReportee(prev => ({
                                                ...prev,
                                                [clarification.clarificationId]: 'OFF-SHORE'
                                              }));
                                            } else if (selectedValue === 'OFF-SHORE') {
                                              setCommentReportee(prev => ({
                                                ...prev,
                                                [clarification.clarificationId]: 'ON-SHORE'
                                              }));
                                            } else {
                                              // Clear reporter if assignee is cleared
                                              setCommentReportee(prev => {
                                                const updated = { ...prev };
                                                delete updated[clarification.clarificationId];
                                                return updated;
                                              });
                                            }
                                          }}
                                        >
                                          <option value="ON-SHORE">ON-SHORE</option>
                                          <option value="OFF-SHORE">OFF-SHORE</option>
                                        </select>
                                      </div>
                                    </div>
                                    <div className="comment-form-row">
                                      <button 
                                        className={`btn-secondary ${(commentText[clarification.clarificationId] || '').trim() ? 'btn-comment-active' : ''}`}
                                        onClick={() => handleAddComment(clarification.clarificationId)}
                                      >
                                        Add Comment
                                      </button>
                                    </div>
                                  </div>
                                </div>
                              </div>
                            )}
                          </div>
                        </>
                      )}
                    </div>
                  ))}
                </div>
              ) : (
                <div className="empty-state">No clarifications found</div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Activity Drawer */}
      <ActivityDrawer
        isOpen={showActivityDrawer}
        onClose={() => setShowActivityDrawer(false)}
        checkId={check?.checkId}
      />

      {/* Move to unknown — account number (optional) */}
      {showMoveToUnknownModal && (
        <div
          className="modal-overlay"
          onClick={closeMoveToUnknownModal}
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000
          }}
        >
          <div
            className="modal-content"
            onClick={(e) => e.stopPropagation()}
            style={{
              background: 'white',
              borderRadius: '8px',
              padding: '24px',
              maxWidth: '440px',
              width: '90%',
              boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)'
            }}
          >
            <h3 style={{ marginTop: 0, marginBottom: '8px', fontSize: '20px', fontWeight: '600', color: '#111827' }}>
              Move check to unknown
            </h3>
            <p style={{ marginBottom: '16px', color: '#6b7280', fontSize: '14px', lineHeight: '1.5' }}>
              Optionally set an unknown account number. Leave blank to keep the existing value on the check.
            </p>
            <div className="form-group" style={{ marginBottom: '24px' }}>
              <label htmlFor="move-to-unknown-account">
                Unknown account number{' '}
                <span style={{ fontWeight: '400', color: '#6b7280' }}>(optional)</span>
              </label>
              <input
                id="move-to-unknown-account"
                type="text"
                value={moveToUnknownAccountInput}
                onChange={(e) => setMoveToUnknownAccountInput(e.target.value)}
                disabled={isMovingToUnknown}
                autoComplete="off"
                placeholder="Enter account number"
              />
            </div>
            <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
              <button
                type="button"
                className="btn-cancel"
                onClick={closeMoveToUnknownModal}
                disabled={isMovingToUnknown}
              >
                Cancel
              </button>
              <button
                type="button"
                className="btn-save"
                onClick={confirmMoveToUnknown}
                disabled={isMovingToUnknown}
              >
                {isMovingToUnknown ? 'Moving…' : 'Move'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Archive/Unarchive Confirmation Modal */}
      {showArchiveConfirm && (
        <div 
          className="modal-overlay"
          onClick={handleArchiveBatchCancel}
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000
          }}
        >
          <div 
            className="modal-content"
            onClick={(e) => e.stopPropagation()}
            style={{
              background: 'white',
              borderRadius: '8px',
              padding: '24px',
              maxWidth: '400px',
              width: '90%',
              boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)'
            }}
          >
            <h3 style={{ marginTop: 0, marginBottom: '16px', fontSize: '20px', fontWeight: '600', color: '#111827' }}>
              {archiveBatchIsArchived ? 'Unarchive Batch' : 'Archive Batch'}
            </h3>
            <p style={{ marginBottom: '24px', color: '#374151', fontSize: '14px', lineHeight: '1.5' }}>
              Are you sure you want to {archiveBatchIsArchived ? 'unarchive' : 'archive'} this batch?
            </p>
            <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
              <button 
                className="btn-cancel" 
                onClick={handleArchiveBatchCancel}
              >
                Cancel
              </button>
              <button 
                className="btn-save" 
                onClick={handleArchiveBatchConfirm}
              >
                {archiveBatchIsArchived ? 'Unarchive' : 'Archive'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Warning Modal - Non-Archived Batch */}
      {showDeleteWarning && (
        <div 
          className="modal-overlay"
          onClick={handleDeleteWarningClose}
        >
          <div 
            className="modal-content"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 className="modal-title-warning">
              Cannot Delete Batch
            </h3>
            <p className="modal-message">
              Only archived batches can be deleted. Please archive the batch first before deleting.
            </p>
            <div className="modal-actions-center">
              <button 
                className="btn-ok" 
                onClick={handleDeleteWarningClose}
              >
                OK
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal - Archived Batch */}
      {showDeleteConfirm && (
        <div 
          className="modal-overlay"
          onClick={handleDeleteConfirmCancel}
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000
          }}
        >
          <div 
            className="modal-content"
            onClick={(e) => e.stopPropagation()}
            style={{
              background: 'white',
              borderRadius: '8px',
              padding: '24px',
              maxWidth: '450px',
              width: '90%',
              boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)'
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', marginBottom: '16px' }}>
              <div style={{
                width: '40px',
                height: '40px',
                borderRadius: '50%',
                background: '#FEF2F2',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                marginRight: '12px'
              }}>
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" style={{ color: '#DC2626' }}>
                  <path d="M3 6H5H21M8 6V4C8 3.46957 8.21071 2.96086 8.58579 2.58579C8.96086 2.21071 9.46957 2 10 2C10.5304 2 11.0391 2.21071 11.4142 2.58579C11.7893 2.96086 12 3.46957 12 4V6M15 6V16C15 16.5304 14.7893 17.0391 14.4142 17.4142C14.0391 17.7893 13.5304 18 13 18H7C6.46957 18 5.96086 17.7893 5.58579 17.4142C5.21071 17.0391 5 16.5304 5 16V6H15Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </div>
              <h3 style={{ margin: 0, fontSize: '20px', fontWeight: '600', color: '#111827' }}>
                Delete Batch
              </h3>
            </div>
            <p style={{ marginBottom: '24px', marginLeft: '52px', color: '#374151', fontSize: '14px', lineHeight: '1.5' }}>
              Are you sure you want to delete this archived batch? This action cannot be undone.
            </p>
            <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
              <button 
                className="btn-cancel" 
                onClick={handleDeleteConfirmCancel}
              >
                Cancel
              </button>
              <button 
                className="btn-save" 
                onClick={handleDeleteConfirm}
                style={{ background: '#DC2626', color: 'white' }}
                onMouseEnter={(e) => e.target.style.background = '#B91C1C'}
                onMouseLeave={(e) => e.target.style.background = '#DC2626'}
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Batch View/Edit Modal */}
      {(viewingBatchId || batchModalMode === 'add') && (() => {
        const batch = viewingBatchId && viewingBatchId !== 'new' ? check?.batches?.find(b => b.batchId === viewingBatchId) : null;
        const isAddMode = batchModalMode === 'add';
        const isEditMode = batchModalMode === 'edit' && editingBatchId === viewingBatchId;
        const isViewMode = batchModalMode === 'view' && batch;
        
        return (
          <div 
            className="modal-overlay"
            onClick={() => {
              if (!isEditMode && !isAddMode) {
                setViewingBatchId(null);
                setBatchModalMode('view');
              }
            }}
            style={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background: 'rgba(0, 0, 0, 0.5)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 1000
            }}
          >
            <div 
              className="modal-content batch-modal"
              onClick={(e) => e.stopPropagation()}
              style={{
                background: 'white',
                borderRadius: '8px',
                padding: '24px',
                maxWidth: '700px',
                width: '90%',
                maxHeight: '90vh',
                overflowY: 'auto',
                boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)'
              }}
            >
              <div className="batch-modal-header">
                <h3 style={{ margin: 0, fontSize: '20px', fontWeight: '600', color: '#111827' }}>
                  {isAddMode ? 'Add Batch' : isEditMode ? 'Edit Batch' : 'Batch Details'}
                </h3>
                <button
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    handleCancelBatch();
                  }}
                  style={{
                    background: 'none',
                    border: 'none',
                    cursor: 'pointer',
                    padding: '4px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}
                >
                  <svg width="24" height="24" viewBox="0 0 20 20" fill="none">
                    <path d="M15 5L5 15M5 5L15 15" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                  </svg>
                </button>
              </div>

              <div className="batch-modal-content">
                {/* Row 1: Batch Type | Source | Batch Date */}
                <div className="batch-modal-row batch-modal-row--3">
                  <div className="batch-modal-field">
                    <label className="batch-modal-label">Batch Type</label>
                    {(isEditMode || isAddMode) ? (
                      <select
                        value={batchFormData.batchType}
                        onChange={(e) => handleBatchFormChange('batchType', e.target.value)}
                        style={{
                          padding: '8px 12px',
                          border: '1px solid #e5e7eb',
                          borderRadius: '6px',
                          fontSize: '14px',
                          width: '100%'
                        }}
                      >
                        <option value="MANUAL">MANUAL</option>
                        <option value="AUTO">AUTO</option>
                      </select>
                    ) : (
                      <div style={{ fontSize: '14px', color: '#111827' }}>{batch?.batchType || 'N/A'}</div>
                    )}
                  </div>
                  <div className="batch-modal-field">
                    <label className="batch-modal-label">Source <span style={{ color: '#dc2626' }}>*</span></label>
                    {(isEditMode || isAddMode) ? (
                      <>
                        <select
                          value={batchFormData.batchSource || 'Athena'}
                          onChange={(e) => handleBatchFormChange('batchSource', e.target.value)}
                          style={{
                            padding: '8px 12px',
                            border: '1px solid #e5e7eb',
                            borderRadius: '6px',
                            fontSize: '14px',
                            width: '100%'
                          }}
                        >
                          <option value="Athena">Athena</option>
                          <option value="Centricity">Centricity</option>
                        </select>
                        {batchValidationErrors.batchSource && (
                          <span style={{ color: '#dc2626', fontSize: '12px' }}>{batchValidationErrors.batchSource}</span>
                        )}
                      </>
                    ) : (
                      <div style={{ fontSize: '14px', color: '#111827' }}>{batch?.batchSource || 'NA'}</div>
                    )}
                  </div>
                  <div className="batch-modal-field">
                    <label className="batch-modal-label">Batch Date <span style={{ color: '#dc2626' }}>*</span></label>
                    {(isEditMode || isAddMode) ? (
                      <>
                        <USDateInput
                          name="batchDate"
                          value={batchFormData.batchDate}
                          onChange={(e) => handleBatchFormChange('batchDate', e.target.value)}
                          placeholder="MM/DD/YYYY"
                          style={{
                            padding: '8px 12px',
                            border: '1px solid #e5e7eb',
                            borderRadius: '6px',
                            fontSize: '14px',
                            width: '100%'
                          }}
                        />
                        {batchValidationErrors.batchDate && (
                          <span style={{ color: '#dc2626', fontSize: '12px' }}>{batchValidationErrors.batchDate}</span>
                        )}
                      </>
                    ) : (
                      <div style={{ fontSize: '14px', color: '#111827' }}>{batch?.batchDate ? formatDateUS(batch.batchDate) : 'N/A'}</div>
                    )}
                  </div>
                </div>

                {/* Row 2: Run Number | Batch Number | EDM Number */}
                <div className="batch-modal-row batch-modal-row--3">
                  <div className="batch-modal-field">
                    <label className="batch-modal-label">Run Number</label>
                    {(isEditMode || isAddMode) ? (
                      <>
                        <input
                          type="text"
                          value={batchFormData.batchType === 'MANUAL' ? '' : batchFormData.batchRunNumber}
                          onChange={(e) => handleBatchFormChange('batchRunNumber', e.target.value)}
                          disabled={batchFormData.batchType === 'MANUAL'}
                          readOnly={batchFormData.batchType === 'MANUAL'}
                          placeholder={batchFormData.batchType === 'MANUAL' ? '' : 'Enter run number'}
                          style={{
                            padding: '8px 12px',
                            border: '1px solid #e5e7eb',
                            borderRadius: '6px',
                            fontSize: '14px',
                            width: '100%',
                            backgroundColor: batchFormData.batchType === 'MANUAL' ? '#f3f4f6' : 'white',
                            cursor: batchFormData.batchType === 'MANUAL' ? 'not-allowed' : 'text'
                          }}
                        />
                        {batchValidationErrors.batchRunNumber && (
                          <span style={{ color: '#dc2626', fontSize: '12px' }}>{batchValidationErrors.batchRunNumber}</span>
                        )}
                      </>
                    ) : (
                      <div style={{ fontSize: '14px', color: '#111827' }}>{batch?.batchRunNumber || 'N/A'}</div>
                    )}
                  </div>
                  <div className="batch-modal-field">
                    <label className="batch-modal-label">Batch Number <span style={{ color: '#dc2626' }}>*</span></label>
                    {(isEditMode || isAddMode) ? (
                      <>
                        <input
                          type="text"
                          value={batchFormData.batchNumber}
                          onChange={(e) => handleBatchFormChange('batchNumber', e.target.value)}
                          style={{
                            padding: '8px 12px',
                            border: '1px solid #e5e7eb',
                            borderRadius: '6px',
                            fontSize: '14px',
                            width: '100%'
                          }}
                        />
                        {batchValidationErrors.batchNumber && (
                          <span style={{ color: '#dc2626', fontSize: '12px' }}>{batchValidationErrors.batchNumber}</span>
                        )}
                      </>
                    ) : (
                      <div style={{ fontSize: '14px', color: '#111827' }}>{batch?.batchNumber || 'N/A'}</div>
                    )}
                  </div>
                  <div className="batch-modal-field">
                    <label className="batch-modal-label">EDM Number</label>
                    {(isEditMode || isAddMode) ? (
                      <input
                        type="text"
                        value={batchFormData.edmNumber}
                        onChange={(e) => handleBatchFormChange('edmNumber', e.target.value)}
                        disabled={batchFormData.batchType !== 'MANUAL'}
                        readOnly={batchFormData.batchType !== 'MANUAL'}
                        style={{
                          padding: '8px 12px',
                          border: '1px solid #e5e7eb',
                          borderRadius: '6px',
                          fontSize: '14px',
                          width: '100%',
                          backgroundColor: batchFormData.batchType !== 'MANUAL' ? '#f3f4f6' : 'white',
                          cursor: batchFormData.batchType !== 'MANUAL' ? 'not-allowed' : 'text'
                        }}
                      />
                    ) : (
                      <div style={{ fontSize: '14px', color: '#111827' }}>{batch?.edmNumber || 'N/A'}</div>
                    )}
                  </div>
                </div>

                {/* Row 3: Invoice Number | Amount | Correction Batch */}
                <div className="batch-modal-row batch-modal-row--3">
                  <div className="batch-modal-field">
                    <label className="batch-modal-label">
                      Invoice Number {
                        isAddMode && check?.unknown === true && 
                        <span style={{ color: '#dc2626' }}>*</span>
                      }
                    </label>
                    {(isEditMode || isAddMode) ? (
                      <>
                        <input
                          type="text"
                          value={batchFormData.invoiceNumber}
                          onChange={(e) => handleBatchFormChange('invoiceNumber', e.target.value)}
                          disabled={check?.unknown !== true}
                          readOnly={check?.unknown !== true}
                          style={{
                            padding: '8px 12px',
                            border: '1px solid #e5e7eb',
                            borderRadius: '6px',
                            fontSize: '14px',
                            width: '100%',
                            backgroundColor: check?.unknown !== true ? '#f3f4f6' : 'white',
                            cursor: check?.unknown !== true ? 'not-allowed' : 'text'
                          }}
                        />
                        {batchValidationErrors.invoiceNumber && (
                          <span style={{ color: '#dc2626', fontSize: '12px' }}>{batchValidationErrors.invoiceNumber}</span>
                        )}
                      </>
                    ) : (
                      <div style={{ fontSize: '14px', color: '#111827' }}>{batch?.invoiceNumber || 'N/A'}</div>
                    )}
                  </div>
                  <div className="batch-modal-field">
                    <label className="batch-modal-label">Amount <span style={{ color: '#dc2626' }}>*</span></label>
                    {(isEditMode || isAddMode) ? (
                      <>
                        <input
                          type="number"
                          value={batchFormData.batchAmount}
                          onChange={(e) => handleBatchFormChange('batchAmount', e.target.value)}
                          style={{
                            padding: '8px 12px',
                            border: '1px solid #e5e7eb',
                            borderRadius: '6px',
                            fontSize: '14px',
                            width: '100%'
                          }}
                        />
                        {batchValidationErrors.batchAmount && (
                          <span style={{ color: '#dc2626', fontSize: '12px' }}>{batchValidationErrors.batchAmount}</span>
                        )}
                      </>
                    ) : (
                      <div style={{ fontSize: '14px', color: '#111827' }}>{formatCurrency(batch?.batchAmount || 0)}</div>
                    )}
                  </div>
                  <div className="batch-modal-field">
                    <label className="batch-modal-label">Correction Batch</label>
                    {(isEditMode || isAddMode) ? (
                      <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', paddingTop: '2px' }}>
                        <input
                          type="checkbox"
                          checked={batchFormData.correctionBatch || false}
                          onChange={(e) => handleBatchFormChange('correctionBatch', e.target.checked)}
                          style={{ width: '18px', height: '18px', cursor: 'pointer' }}
                        />
                        <span>{batchFormData.correctionBatch ? 'Yes' : 'No'}</span>
                      </label>
                    ) : (
                      <div style={{ fontSize: '14px', color: '#111827', paddingTop: '2px' }}>
                        <span className={`status-badge ${batch?.isCorrectionBatch ? 'status-active' : 'status-archived'}`}>
                          {batch?.isCorrectionBatch ? 'Yes' : 'No'}
                        </span>
                      </div>
                    )}
                  </div>
                </div>

                {/* Row 4: Notes (full row) */}
                <div className="batch-modal-field batch-modal-field-full">
                  <label className="batch-modal-label">Notes</label>
                  {(isEditMode || isAddMode) ? (
                    <textarea
                      value={batchFormData.batchNotes}
                      onChange={(e) => handleBatchFormChange('batchNotes', e.target.value)}
                      rows="3"
                      style={{
                        padding: '8px 12px',
                        border: '1px solid #e5e7eb',
                        borderRadius: '6px',
                        fontSize: '14px',
                        width: '100%',
                        resize: 'vertical'
                      }}
                    />
                  ) : (
                    <div style={{ fontSize: '14px', color: '#111827', whiteSpace: 'pre-wrap' }}>{batch?.batchNotes || 'N/A'}</div>
                  )}
                </div>

                {/* Created / Updated (view only) */}
                {isViewMode && batch && (
                  <div className="batch-modal-row">
                    <div className="batch-modal-field">
                      <label className="batch-modal-label">Created</label>
                      <UserTimestamp
                        userId={batch.createdBy}
                        dateTime={batch.createdAt}
                        action="created"
                        getUserName={getUserName}
                        getUserById={getUserById}
                      />
                    </div>
                    <div className="batch-modal-field">
                      <label className="batch-modal-label">Updated</label>
                      <UserTimestamp
                        userId={batch.updatedBy}
                        dateTime={batch.updatedAt}
                        action="updated"
                        getUserName={getUserName}
                        getUserById={getUserById}
                      />
                    </div>
                  </div>
                )}

                {/* DRL Allocations Section */}
                {isViewMode && batch && hasDrlAllocations(batch) && (
                  <div className="batch-modal-field batch-modal-field-full" style={{ marginTop: '24px', paddingTop: '24px', borderTop: '1px solid #e5e7eb' }}>
                    <label className="batch-modal-label" style={{ marginBottom: '12px', fontSize: '16px', fontWeight: '600' }}>
                      DRL Allocations ({getDrlAllocationCount(batch)})
                    </label>
                    <div style={{ 
                      backgroundColor: '#f9fafb', 
                      borderRadius: '8px', 
                      border: '1px solid #e5e7eb',
                      overflow: 'hidden'
                    }}>
                      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '14px' }}>
                        <thead style={{ backgroundColor: '#f3f4f6' }}>
                          <tr>
                            <th style={{ padding: '12px', textAlign: 'left', fontWeight: '600', color: '#374151', borderBottom: '1px solid #e5e7eb' }}>DRL Number</th>
                            <th style={{ padding: '12px', textAlign: 'left', fontWeight: '600', color: '#374151', borderBottom: '1px solid #e5e7eb' }}>Batch Number</th>
                            <th style={{ padding: '12px', textAlign: 'right', fontWeight: '600', color: '#374151', borderBottom: '1px solid #e5e7eb' }}>Allocated Amount</th>
                            <th style={{ padding: '12px', textAlign: 'right', fontWeight: '600', color: '#374151', borderBottom: '1px solid #e5e7eb' }}>DRL Batch Amount</th>
                          </tr>
                        </thead>
                        <tbody>
                          {batch.drlAllocations.map((allocation, idx) => (
                            <tr 
                              key={allocation.allocationId || idx}
                              style={{
                                borderBottom: idx < batch.drlAllocations.length - 1 ? '1px solid #e5e7eb' : 'none'
                              }}
                            >
                              <td style={{ padding: '12px', color: '#111827' }}>
                                {allocation.drlNumber || 'N/A'}
                              </td>
                              <td style={{ padding: '12px', color: '#111827' }}>
                                {allocation.drlBatchNumber ? (
                                  <button
                                    type="button"
                                    onClick={() => navigate(`/dit-drl/${allocation.drlId}?batchId=${allocation.drlBatchId}`)}
                                    style={{
                                      background: 'none',
                                      border: 'none',
                                      color: '#3b82f6',
                                      cursor: 'pointer',
                                      textDecoration: 'underline',
                                      padding: 0,
                                      fontSize: '14px'
                                    }}
                                    onMouseEnter={(e) => e.target.style.color = '#2563eb'}
                                    onMouseLeave={(e) => e.target.style.color = '#3b82f6'}
                                    title="View DRL batch"
                                  >
                                    {allocation.drlBatchNumber}
                                  </button>
                                ) : 'N/A'}
                              </td>
                              <td style={{ padding: '12px', color: '#111827', textAlign: 'right', fontWeight: '500' }}>
                                {formatCurrency(allocation.allocatedAmount || 0)}
                              </td>
                              <td style={{ padding: '12px', color: '#6b7280', textAlign: 'right', fontSize: '13px' }}>
                                {formatCurrency(allocation.drlBatchAmount || 0)}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}
              </div>

              {(isEditMode || isAddMode) && (
                <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end', marginTop: '24px' }}>
                  <button
                    className="btn-cancel"
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      handleCancelBatch();
                    }}
                  >
                    Cancel
                  </button>
                  <button
                    className="btn-primary"
                    onClick={async (e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      if (isAddMode) {
                        await handleAddBatch();
                      } else {
                        await handleUpdateBatch();
                        setViewingBatchId(null);
                        setBatchModalMode('view');
                      }
                    }}
                    disabled={isAddMode ? isCreatingBatch : isUpdatingBatch}
                  >
                    {isAddMode 
                      ? (isCreatingBatch ? 'Creating...' : 'Add Batch')
                      : (isUpdatingBatch ? 'Updating...' : 'Save Changes')
                    }
                  </button>
                </div>
              )}
            </div>
          </div>
        );
      })()}

      {/* DRL Allocation Modal */}
      {showDrlAllocationModal && selectedBatchForDrlAllocation && (
        <div 
          className="modal-overlay"
          onClick={() => {
            setShowDrlAllocationModal(false);
            setSelectedBatchForDrlAllocation(null);
          }}
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
            padding: '16px'
          }}
        >
          <div 
            className="modal-content"
            onClick={(e) => e.stopPropagation()}
            style={{
              background: 'white',
              borderRadius: '8px',
              padding: '20px',
              maxWidth: '650px',
              width: '100%',
              maxHeight: '85vh',
              overflowY: 'auto',
              boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)'
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '16px' }}>
              <h3 style={{ margin: 0, fontSize: '18px', fontWeight: '600', color: '#111827' }}>
                DRL Allocations - Batch {selectedBatchForDrlAllocation.batchNumber || 'N/A'}
              </h3>
              <button
                onClick={() => {
                  setShowDrlAllocationModal(false);
                  setSelectedBatchForDrlAllocation(null);
                }}
                style={{
                  background: 'transparent',
                  border: 'none',
                  cursor: 'pointer',
                  padding: '4px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: '#6b7280'
                }}
                onMouseEnter={(e) => e.target.style.color = '#111827'}
                onMouseLeave={(e) => e.target.style.color = '#6b7280'}
              >
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                  <path d="M18 6L6 18M6 6L18 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </button>
            </div>

            {hasDrlAllocations(selectedBatchForDrlAllocation) ? (
              <div>
                <div style={{ 
                  marginBottom: '16px', 
                  padding: '12px', 
                  backgroundColor: '#f0fdf4', 
                  borderRadius: '6px', 
                  border: '1px solid #86efac',
                  fontSize: '13px'
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                    <svg width="16" height="16" viewBox="0 0 20 20" fill="none" style={{ color: '#10b981', flexShrink: 0 }}>
                      <path d="M10 18.3333C14.6024 18.3333 18.3333 14.6024 18.3333 10C18.3333 5.39763 14.6024 1.66667 10 1.66667C5.39763 1.66667 1.66667 5.39763 1.66667 10C1.66667 14.6024 5.39763 18.3333 10 18.3333Z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                      <path d="M7.5 10L9.16667 11.6667L12.5 8.33333" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                    <span style={{ fontWeight: '600', color: '#111827' }}>
                      {getDrlAllocationCount(selectedBatchForDrlAllocation)} DRL Allocation(s) Found
                    </span>
                  </div>
                </div>

                <div style={{ 
                  border: '1px solid #e5e7eb', 
                  borderRadius: '6px', 
                  overflow: 'hidden',
                  marginBottom: '16px'
                }}>
                  <div style={{ maxHeight: '350px', overflowY: 'auto' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
                      <thead style={{ backgroundColor: '#f9fafb', position: 'sticky', top: 0, zIndex: 10 }}>
                        <tr>
                          <th style={{ padding: '10px 8px', textAlign: 'left', fontWeight: '600', color: '#374151', borderBottom: '1px solid #e5e7eb', fontSize: '12px' }}>DRL Number</th>
                          <th style={{ padding: '10px 8px', textAlign: 'left', fontWeight: '600', color: '#374151', borderBottom: '1px solid #e5e7eb', fontSize: '12px' }}>Batch Number</th>
                          <th style={{ padding: '10px 8px', textAlign: 'right', fontWeight: '600', color: '#374151', borderBottom: '1px solid #e5e7eb', fontSize: '12px' }}>Allocated</th>
                          <th style={{ padding: '10px 8px', textAlign: 'right', fontWeight: '600', color: '#374151', borderBottom: '1px solid #e5e7eb', fontSize: '12px' }}>DRL Batch Amt</th>
                          <th style={{ padding: '10px 8px', textAlign: 'right', fontWeight: '600', color: '#374151', borderBottom: '1px solid #e5e7eb', fontSize: '12px' }}>Remaining</th>
                        </tr>
                      </thead>
                      <tbody>
                        {selectedBatchForDrlAllocation.drlAllocations.map((allocation, idx) => (
                          <tr 
                            key={allocation.allocationId || idx}
                            style={{
                              backgroundColor: idx % 2 === 0 ? 'white' : '#f9fafb',
                              borderBottom: idx < selectedBatchForDrlAllocation.drlAllocations.length - 1 ? '1px solid #e5e7eb' : 'none'
                            }}
                            onMouseEnter={(e) => e.target.closest('tr').style.backgroundColor = '#f3f4f6'}
                            onMouseLeave={(e) => e.target.closest('tr').style.backgroundColor = idx % 2 === 0 ? 'white' : '#f9fafb'}
                          >
                            <td style={{ padding: '10px 8px', color: '#111827', fontSize: '13px' }}>
                              {allocation.drlNumber || 'N/A'}
                            </td>
                            <td style={{ padding: '10px 8px', color: '#111827', fontWeight: '500', fontSize: '13px' }}>
                              {allocation.drlBatchNumber ? (
                                <button
                                  type="button"
                                  onClick={() => {
                                    setShowDrlAllocationModal(false);
                                    setSelectedBatchForDrlAllocation(null);
                                    navigate(`/dit-drl/${allocation.drlId}?batchId=${allocation.drlBatchId}`);
                                  }}
                                  style={{
                                    background: 'none',
                                    border: 'none',
                                    color: '#3b82f6',
                                    cursor: 'pointer',
                                    textDecoration: 'underline',
                                    padding: 0,
                                    fontSize: '13px',
                                    fontWeight: '500',
                                    textAlign: 'left'
                                  }}
                                  onMouseEnter={(e) => e.target.style.color = '#2563eb'}
                                  onMouseLeave={(e) => e.target.style.color = '#3b82f6'}
                                  title="View DRL batch"
                                >
                                  {allocation.drlBatchNumber}
                                </button>
                              ) : (
                                <span style={{ color: '#9ca3af', fontSize: '13px' }}>N/A</span>
                              )}
                            </td>
                            <td style={{ padding: '10px 8px', color: '#111827', textAlign: 'right', fontWeight: '600', fontSize: '13px' }}>
                              {formatCurrency(allocation.allocatedAmount || 0)}
                            </td>
                            <td style={{ padding: '10px 8px', color: '#6b7280', textAlign: 'right', fontSize: '12px' }}>
                              {formatCurrency(allocation.drlBatchAmount || 0)}
                            </td>
                            <td style={{ padding: '10px 8px', color: '#059669', textAlign: 'right', fontSize: '12px', fontWeight: '500' }}>
                              {formatCurrency(allocation.drlBatchRemainingAllocatableAmount || 0)}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            ) : (
              <div style={{ 
                padding: '32px 20px', 
                textAlign: 'center', 
                color: '#6b7280' 
              }}>
                <svg width="40" height="40" viewBox="0 0 24 24" fill="none" style={{ margin: '0 auto 12px', color: '#9ca3af' }}>
                  <path d="M10 18.3333C14.6024 18.3333 18.3333 14.6024 18.3333 10C18.3333 5.39763 14.6024 1.66667 10 1.66667C5.39763 1.66667 1.66667 5.39763 1.66667 10C1.66667 14.6024 5.39763 18.3333 10 18.3333Z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                  <path d="M10 6.66667V10M10 13.3333H10.0083" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
                <p style={{ fontSize: '14px', margin: '0' }}>No DRL allocations found for this batch</p>
              </div>
            )}

            <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '16px', paddingTop: '16px', borderTop: '1px solid #e5e7eb' }}>
              <button 
                className="btn-primary" 
                onClick={() => {
                  setShowDrlAllocationModal(false);
                  setSelectedBatchForDrlAllocation(null);
                }}
                style={{ minWidth: '80px', padding: '8px 16px', fontSize: '14px' }}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CheckDetails;
